import { docData, doc, collectionChanges } from "rxfire/firestore"
import { bindCallback, of, concat, from, defer, Subject, merge as mergeN, combineLatest } from 'rxjs'
import { catchError, retryWhen, delay as retryDelay, filter, map, flatMap, concatMap, switchMap, take, merge  } from 'rxjs/operators'
import axios from 'axios'
import { randomElement, clone, capitalize, mergeFields, formatAddress } from './Util.js'
import {LinkPreviewer} from "./LinkPreviewer";
import phone from 'phone'
import { isDesktop } from './Platform.js'
import { resolveDialect } from './Lang.js'
import { observeMicInput } from './Audio.js'
import { encodeMP3 } from './MP3.js'
import { saveAs } from 'file-saver'
import { streamReply } from './Stream.js'
import { autocompleteStreamReader } from './AutocompleteStream.js'
import { unmute } from './unmute.js'
import { createTokenBalanceAccumulator } from './TokenPacks.js'
import { TokenPurchaseClient, waitForStripeFulfillment } from './TokenPurchases.js'
import { TokenBilling } from './TokenBilling.js'
import { authenticateWithGoogle } from './GoogleAuth.js'
import { authenticateWithApple, canSignInWithApple, revokeAppleForAccountDeletion } from './AppleAuth.js'
import { verifyAndMergePhone } from './AccountMerge.js'
import { NativeAuthGeneration } from './NativeAuthGeneration.js'
import { readInferenceProviderSelection, selectInferenceProvider, usesDeviceSubscriptions } from './InferenceProviderSelection.js'
import { effectiveAutocorrectionLevel, normalizeKeyboardSettings } from './KeyboardSettings.js'
import {
  hasAppleInteropBridge,
  hasNativeCapability,
  hasNativeIdentityBridge,
  hasNativeMessageBridge,
  isIntelliKeyMacApp,
  isIntelliKeyWindowsApp
} from './NativeEnvironment.js'
import { normalizeMacSetupStatus } from './MacSetupStatus.js'
import { normalizeWindowsSetupStatus } from './WindowsSetupStatus.js'
import { requiredFirebaseAuthHeaders } from './FirebaseAuthHeaders.js'
import {
  autocompletePayload,
  autocorrectPayload
} from './KeyboardInferencePayload.js'
import {
  WRITING_ASSISTANT_DICTATION_PAGE,
  focusOrOpenWritingAssistantAtEnd
} from './WritingAssistantLaunch.js'

const consoleLog = (...args) => {
  //console.log(...args)
}

const debugLog = (...args) => {
  consoleLog(...args)
}

// The phone-local prototype still needs hosted account/catalog services. It
// must not depend on a Mac API process to restore the native Firebase identity.
const keyboardApiServer = process.env.REACT_APP_KEYBOARD_API_SERVER || (hasNativeCapability('localOpenAISubscription')
  ? 'https://keyboard-api-v2-o4pon4eraa-uc.a.run.app'
  : 'http://192.168.0.237:4317')

try {
  speechSynthesis.getVoices()
} catch (ignored) {
  
}
let LANG
const MAX_TOKENS = 30
const SOURCE = new URLSearchParams(window.location.search).get('src')
const WHISPER = new URLSearchParams(window.location.search).get('whisper')
const Token = new URLSearchParams(window.location.search).get('token')
const InviteCode = new URLSearchParams(window.location.search).get('referralCode')

const WINDOWS_WRITING_REQUEST_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/
const WINDOWS_DOCUMENT_LEASE = /^[0-9a-f]{64}$/
const WINDOWS_DOCUMENT_LEASE_GETTER =
  '__INTELLIKEY_GET_NATIVE_DOCUMENT_LEASE__'
const WINDOWS_DOCUMENT_LEASE_READY_EVENT =
  'intellikey-native-document-lease-ready'
const WINDOWS_PENDING_NATIVE_MESSAGE_LIMIT = 32

export const attachWindowsDocumentLease = (message, lease) => {
  if (!message || typeof message !== 'object' ||
      typeof lease !== 'string' || !WINDOWS_DOCUMENT_LEASE.test(lease)) {
    return null
  }
  return { ...message, webDocumentLease: lease }
}

export const prepareNativeMessage = (message, isWindows, lease) =>
  isWindows ? attachWindowsDocumentLease(message, lease) : message

export const enqueuePendingWindowsNativeMessage = (
  pending,
  message,
  maximumMessages = WINDOWS_PENDING_NATIVE_MESSAGE_LIMIT
) => {
  if (!Array.isArray(pending) ||
      !message ||
      typeof message !== 'object' ||
      !Number.isInteger(maximumMessages) ||
      maximumMessages < 1) {
    return false
  }

  // Debug traffic has no state to preserve and must not consume the short
  // pre-bootstrap window needed by identity and writing messages.
  if (message.type === 'log') return false

  const coalesced = message.type === 'ready' ||
    message.type === 'token' ||
    message.type === 'signOut'
  if (coalesced) {
    const prior = [...pending].reverse().find(item => item.type === message.type)
    if (prior &&
        Number.isSafeInteger(prior.authGeneration) &&
        Number.isSafeInteger(message.authGeneration) &&
        message.authGeneration < prior.authGeneration) {
      return true
    }
    for (let index = pending.length - 1; index >= 0; --index) {
      if (pending[index].type === message.type) pending.splice(index, 1)
    }
  }

  if (pending.length >= maximumMessages) {
    if (message.type !== 'signOut') return false

    // An exact sign-out is the fail-closed identity boundary. Reserve room by
    // evicting one ordinary pre-bootstrap message, then retain the latest
    // sign-out generation at the end of the original delivery order.
    const evict = pending.findIndex(item => item.type !== 'signOut')
    if (evict < 0) return false
    pending.splice(evict, 1)
  }

  pending.push({ ...message })
  return true
}

export const makeWritingInput = (
  document,
  cursorOffset,
  buttonId,
  windowsRequestId = null,
  selectionAutocorrect = false
) => {
  const input = windowsRequestId
    ? { document, cursorOffset, buttonId, requestId: windowsRequestId }
    : { document, cursorOffset, buttonId }
  return selectionAutocorrect
    ? { ...input, selectionAutocorrect: true }
    : input
}

export const matchesWindowsAuthObservation = (
  nativeAuthGeneration,
  currentUID,
  authGeneration,
  authUID
) => Boolean(
  nativeAuthGeneration &&
  typeof nativeAuthGeneration.isCurrent === 'function' &&
  Number.isSafeInteger(authGeneration) &&
  authGeneration > 0 &&
  (authUID === null || typeof authUID === 'string') &&
  nativeAuthGeneration.isCurrent(
    { authGeneration, authUID },
    currentUID
  )
)

export const matchesWindowsNativeReply = (
  nativeAuthGeneration,
  currentUID,
  expected,
  message
) => Boolean(
  expected &&
  message &&
  expected.authGeneration === message.authGeneration &&
  expected.authUID === message.authUID &&
  matchesWindowsAuthObservation(
    nativeAuthGeneration,
    currentUID,
    message.authGeneration,
    message.authUID
  )
)

export const attachWindowsAuthObservation = (message, observation) => {
  if (!message ||
      typeof message !== 'object' ||
      !observation ||
      !Number.isSafeInteger(observation.authGeneration) ||
      observation.authGeneration < 1 ||
      !(observation.authUID === null ||
        typeof observation.authUID === 'string')) {
    return null
  }
  return { ...message, ...observation }
}

// A Windows host edit is a one-shot capability. The request ID is captured by
// the callbacks installed for that exact edit instead of being looked up when
// a potentially delayed UI callback eventually runs.
export class WindowsWritingRequestFence {
  requestId = null

  begin = requestId => {
    if (typeof requestId !== 'string' ||
        !WINDOWS_WRITING_REQUEST_ID.test(requestId) ||
        this.requestId !== null) {
      return null
    }
    this.requestId = requestId
    return requestId
  }

  take = requestId => {
    if (typeof requestId !== 'string' || requestId !== this.requestId) {
      return null
    }
    this.requestId = null
    return requestId
  }

  cancel = requestId => this.take(requestId) !== null
}

const delay = seconds => new Promise(resolve => setTimeout(resolve, seconds*1000))

const xmlEscape = xml => {
  if (!xml) return ''
  return xml.replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

const xml2json = xml => {
  try {
    var obj = {};
    if (xml.children.length > 0) {
      for (var i = 0; i < xml.children.length; i++) {
        var item = xml.children.item(i);
        var nodeName = item.nodeName;

        if (typeof (obj[nodeName]) == "undefined") {
          obj[nodeName] = xml2json(item);
        } else {
          if (typeof (obj[nodeName].push) == "undefined") {
            var old = obj[nodeName];

            obj[nodeName] = [];
            obj[nodeName].push(old);
          }
          obj[nodeName].push(xml2json(item));
        }
      }
    } else {
      obj = xml.textContent;
    }
    return obj;
  } catch (e) {
      consoleLog(e.message);
  }
}

class WhisperVoiceRecognizer {
  instructionSubject = new Subject()
  isActiveSubject = new Subject()
  isActive = false

  observeInstruction = () => {
    return this.instructionSubject
  }

  setLang = lang => {
    this.lang = lang
    //debugger
  }

  observeIsActive = () => {
    return concat(this.isActive, this.isActiveSubject)
  }

  start = async () => {
    if (!this.vad) {
      this.vad = await window.vad.MicVAD.new({
        onSpeechEnd: async audio => {
          consoleLog("speech end", this.lang)
          const blob = encodeMP3([audio])
          //saveAs(blob, "audio.mp3")
          //return
          const file = new File([blob], 'audio.mp3', {
            type: 'audio/mp3'
          })
          const response = await axios.post('http://localhost:8080/transcribe?lang='+(this.lang || ''),
                                            file)
          const { text } = response.data
          this.instructionSubject.next(text)
        }
      })
    }
    this.vad.start()
    this.isActiveSubject.next(this.isActive = true)
  }

  stop = () => {
    this.isActive = false
    this.isActiveSubject.next(false)
    this.vad.pause()
  }
}


class SystemVoiceRecognizer {

  constructor (autocorrect) {
    this.autocorrect = autocorrect
  }

  instructionSubject = new Subject()

  observeInstruction = () => {
    return this.instructionSubject
  }

  onResult = async event => {
    let prompt = ''
    consoleLog("onResult", event)
    for (var i = event.resultIndex; i < event.results.length; ++i) {
      if (event.results[i].isFinal) {
        prompt += event.results[i][0].transcript
        break
      }
    }
    this.instructionSubject.next(prompt)
  }

  stop = () => {
    this.active = false
    consoleLog("STOP RECOGNITION")
    if (this.recognition) {
      this.recognition.abort()
      this.recognition.stop()
    }
    this.onActiveSubject.next(false)
  }

  start = () => {
    if (this.active) return
    this.active = true
    if (!this.recognition) {
      const recognition = new window.webkitSpeechRecognition()
      if (LANG) {
        recognition.lang = LANG
      }
      consoleLog("LANG", LANG)
      recognition.continuous = true
      recognition.interimResults = false
      recognition.onstart = this.onStart
      recognition.onresult =  this.onResult
      recognition.onerror = this.onError
      recognition.onend = this.onEnd
      this.recognition = recognition
    }
    this.recognition.start()
    consoleLog("START RECOGNITION")
    this.onActiveSubject.next(true)
  }

  onError = event =>{
    consoleLog('error:', event)
  }

  onActiveSubject = new Subject()

  observeIsActive = () => {
    return concat(of(this.active), this.onActiveSubject)
  }

  onEnd = event => {
    consoleLog('end:', event)
    if (this.active) {
      this.active = false
      this.start()
    }
  }

  setLang = lang => {
    if (LANG != lang) {
      const wasActive = this.isActive
      //debugger
      if (wasActive) {
        this.stop()
      }
      LANG = lang
      this.recognition = null
      if (wasActive) {
        this.start()
      }
    }
  }
}

class VoiceRecognizer {
  setLang = lang => {
    this.lang = lang
    if (this.impl) {
    }
  }

  canUseWhisper = () => {
    return false
  }

  start = () => {
    if (!this.impl) {
      if (this.canUseWhisper()) {
        this.impl = new WhisperVoiceRecognizer()
      } else {
        this.impl = new SystemVoiceRecognizer(this.autocorrect)
      }
      this.sub1 = this.impl.observeInstruction().subscribe(instruction => {
        this.instructionSubject.next(instruction)
      })
      this.sub2 = this.impl.observeIsActive().subscribe(isActive => {
        this.isActiveSubject.next(isActive)
      })
    }
  }
  stop = () => {
    this.impl.stop()
    this.impl = null
    this.sub1.unsubscribe()
    this.sub2.unsubscribe()
    this.sub1 = null
    this.sub2 = null
  }

  instructionSubject = new Subject()
  isActiveSubject = new Subject()
  
  observeInstruction = () => {
    return this.instructionSubject
  }

  observeIsActive = () => {
    const impl = this.impl
    if (impl) {
      return concat(impl.isActive, this.isActiveSubject)
    }
    return this.isActiveSubject
  }
}

export class Me {
  nativeAuthGeneration = new NativeAuthGeneration()
  windowsWritingRequests = new WindowsWritingRequestFence()
  pendingWindowsNativeMessages = []

  isNative = () => {
    return hasNativeMessageBridge() ||
      (typeof window !== 'undefined' && window.electronAPI)
  }

  sendNativeMessage = msg => {
    if (this.isNative()) {
      const isWindows = isIntelliKeyWindowsApp()
      let lease = null
      if (isWindows) {
        const getter = window[WINDOWS_DOCUMENT_LEASE_GETTER]
        lease = typeof getter === 'function' ? getter() : null
      }
      const message = prepareNativeMessage(msg, isWindows, lease)
      if (isWindows) {
        if (!message) {
          enqueuePendingWindowsNativeMessage(
            this.pendingWindowsNativeMessages,
            msg
          )
          return false
        }
      }

      const body = JSON.stringify(message)
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(body)
        return true
      }
      if (hasAppleInteropBridge()) {
        window.webkit.messageHandlers.interOp.postMessage(body)
        return true
      }
      if (window.chrome &&
          window.chrome.webview &&
          typeof window.chrome.webview.postMessage === 'function') {
        window.chrome.webview.postMessage(body)
        return true
      }
    }
    return false
  }

  flushPendingWindowsNativeMessages = () => {
    if (!isIntelliKeyWindowsApp() ||
        this.pendingWindowsNativeMessages.length === 0) {
      return
    }
    const pending = this.pendingWindowsNativeMessages.splice(0)
    pending.forEach(message => this.sendNativeMessage(message))
  }

  isKeyboardExtension = () => {
    return !!Token 
  }

  isContainingApp = () => {
    return !Token 
  }

  nativeLog = msg => {
    if (this.isNative()) {
      this.sendNativeMessage({
        type: 'log',
        message: msg
      })
    } else {
      consoleLog(msg)
    }
  }

  verifyAppleReceipt = async (transactionId, receiptData) => {
    const session = this.nativeAuthGeneration.snapshot()
    consoleLog("verifyAppleReceipt")
    const func = this.getFunc('api/purchases/apple/verify')
    const response = await func({transactionId, receiptData})
    //debugger
    const { verified } = response.data
    if (verified && this.nativeAuthGeneration.isCurrent(session, this.firebase.auth().currentUser?.uid)) {
      this.finishAppleTransaction(transactionId)
    }
    return response.data
  }

  finishAppleTransaction = transactionId => {
    if (!hasAppleInteropBridge()) {
      return null
    }
    consoleLog("finish apple transaction")
    const reqId = String(++this.reqId)
    const msg = {
      type: 'verified',
      transactionId,
      reqId
    }
    const p =  new Promise((resolve, reject) => {
      this.ops[reqId] = {
        resolve, reject
      }
      window.webkit.messageHandlers.interOp.postMessage(JSON.stringify(msg))
    })
    p.then(() => {
      consoleLog('transaction complete')
    })
    return null
  }

  verified = {}
  verifiedTransactionSubject = new Subject()
  
  observeVerifiedTransactions = () => {
    return concat(from(Object.keys(verified)), this.verifiedTransactionSubject)
  }

  refreshingToken = false
  tokenRefreshSubject = new Subject()
  observeKeyboardLogin = () => {
    if (this.refreshingToken) {
      return concat(of(this.refreshingToken), this.tokenRefreshSubject)
    }
    return this.tokenRefreshSubject
  }

  keyboardLoginError = null
  keyboardLoginAttempt = 0

  refreshKeyboardToken = async () => {
    if (this.refreshingToken || !this.self) return false
    const snapshot = this.nativeAuthGeneration.snapshot()
    const attempt = ++this.keyboardLoginAttempt
    const isCurrent = () => attempt === this.keyboardLoginAttempt &&
      this.nativeAuthGeneration.isCurrent(snapshot, this.firebase.auth().currentUser?.uid)
    this.keyboardLoginError = null
    this.refreshingToken = true
    this.tokenRefreshSubject.next(true)
    let timeout
    try {
      await Promise.race([
        (async () => {
          await this.synchronizeNativeIdentity()
          if (!isCurrent()) throw new Error('account-changed')
          const token = await this.getIdToken()
          if (!isCurrent()) throw new Error('account-changed')
          if (!token || !this.sendNativeMessage({ type: 'token', token, ...snapshot })) {
            throw new Error('keyboard-handoff-failed')
          }
        })(),
        new Promise((_, reject) => {
          timeout = setTimeout(() => reject(new Error('keyboard-login-timeout')), 20000)
        })
      ])
      return true
    } catch (_) {
      if (isCurrent()) {
        this.keyboardLoginError = 'Could not sign the keyboard in. Check your connection and try again.'
      }
      return false
    } finally {
      clearTimeout(timeout)
      if (attempt === this.keyboardLoginAttempt) {
        // Retire timed-out work before it can publish a late token.
        this.keyboardLoginAttempt++
        this.refreshingToken = false
        this.tokenRefreshSubject.next(false)
      }
    }
  }

  pageRequestSubject = new Subject()
  writingAssistantVoiceInputCommandSubject = new Subject()

  observePageRequest = () => {
    if (this.requestedPage) {
      return concat(of(this.requestedPage), this.pageRequestSubject)
    }
    return this.pageRequestSubject
  }

  requestPage = page => {
    this.requestedPage = page
    this.pageRequestSubject.next(page)
  }

  completePageRequest = page => {
    if (this.requestedPage === page) {
      this.requestedPage = null
    }
  }

  observeWritingAssistantVoiceInputCommand = () => {
    return this.writingAssistantVoiceInputCommandSubject
  }

  publishWritingAssistantVoiceInputState = state => {
    if (!isIntelliKeyMacApp()) return false
    return this.sendNativeMessage({
      type: 'writingAssistantVoiceInputState',
      ...state
    })
  }
  
  nativeInit () {
    if (!this.nativeInitDone) {
      this.nativeInitDone = true
      if (isIntelliKeyWindowsApp()) {
        window.addEventListener(
          WINDOWS_DOCUMENT_LEASE_READY_EVENT,
          this.flushPendingWindowsNativeMessages
        )
      }
      window.electronReply = message => {
        const reply = JSON.parse(message)
        const { id, type } = reply
        const call = this.electronCalls[id]
        delete this.electronCalls[id]
        if (call) {
          call[type](reply[type])
        }
      }
      
      if (typeof window !== 'undefined' && window.ReactNativeWebView) {
        this.nativeLog("native init done: "+window.postMessage);
        this.sendNativeMessage({
          type: 'config',
          config: this.config
        })
      }
      window.openHomePage = () => {
        this.requestPage('home')
      }
      window.ask = (question) => {
        this.requestPage('ask')
      }
      window.onAppleTransactionFinished = (reqId) => {
        const op = this.ops[reqId]
        delete this.ops[reqId]
        if (op) {
          op.resolve()
        }
      }
      window.returnDeviceId = id => {
        if (!this.self) {
          this.firebase.auth().signInWithCustomToken(id)
        }
      }
      window.handleInvitation = link => {
        this.handleInvitation(link)
      }
      window.handleReferral = invitedBy => {
        this.handleReferral(invitedBy)
      }
      window.refreshToken = this.refreshKeyboardToken
      window.sendButtons = () => {
        this.sendButtons()
      }
      window.verifyAppleTransaction = (transactionId, jws) => {
        const session = this.nativeAuthGeneration.snapshot()
        this.verifyAppleReceipt(transactionId, jws).then(result => {
          if (result.verified && this.nativeAuthGeneration.isCurrent(session, this.firebase.auth().currentUser?.uid)) {
            this.verified[transactionId] = true
            this.verifiedTransactionSubject.next(transactionId)
          }
        }).catch(() => {})
        return null
      }
      window.onReceipt = (reqId, receiptAsBase64) => {
        const op = this.ops[reqId]
        delete this.ops[reqId]
        const p = this.verifyAppleReceipt(undefined, receiptAsBase64)
        if (op) {
          p.then(op.resolve).catch(op.reject)
        }
        return null
      }
      window.onPurchaseResult = (reqId, transactionId, purchased, jws) => {
        consoleLog("onPurchaseResult", { purchased: Boolean(purchased) })
        const op = this.ops[reqId]
        delete this.ops[reqId]
        if (op) op.resolve({transactionId, purchased: purchased === true || purchased === 'true', jws})
        return null
      }
      window.edit = (
        document,
        cursorOffset,
        buttonId,
        requestId,
        authGeneration,
        authUID,
        selectionAutocorrect
      ) => {
        const isWindows = isIntelliKeyWindowsApp()
        if (isWindows && !matchesWindowsAuthObservation(
          this.nativeAuthGeneration,
          this.firebase.auth().currentUser?.uid,
          authGeneration,
          authUID
        )) {
          return false
        }
        const windowsRequestId = isWindows
          ? this.windowsWritingRequests.begin(requestId)
          : null
        if (isWindows && !windowsRequestId) {
          return false
        }
        if (windowsRequestId) {
          const authObservation = { authGeneration, authUID }
          // React receives these functions as props for this edit. Keeping the
          // exact ID in each closure prevents an old A callback from reading a
          // newer mutable B ID after A was cancelled.
          this.cancelKeyboardOutput = document =>
            this.cancelKeyboardOutputForRequest(
              windowsRequestId,
              document,
              authObservation
            )
          this.sendKeyboardOutput = output =>
            this.sendKeyboardOutputForRequest(
              windowsRequestId,
              output,
              authObservation
            )
        }
        this.keyboardActive = true
        this.keyboardActiveSubject.next(true)
        this.keyboardInput = makeWritingInput(
          document,
          cursorOffset,
          buttonId,
          windowsRequestId,
          selectionAutocorrect === true
        )
        this.keyboardSubject.next(this.keyboardInput)
        this.reportProgress('appEdit')
        return true
      }
      window.cancelIntelliKeyEdit = requestId => {
        if (!isIntelliKeyWindowsApp() ||
            !this.windowsWritingRequests.cancel(requestId)) {
          return false
        }
        this.keyboardInput = null
        this.keyboardActive = false
        this.keyboardActiveSubject.next(false)
        return true
      }
      window.willEnterBackground = () => {
        this.keyboardActive = false
        this.keyboardActiveSubject.next(false)
      }
      window.requestFullAccess = () => {
        this.fullAccessRequested = true
        this.fullAccessRequestSubject.next(true)
      }
      window.reportKeyboardStatus = (isInstalled, hasFullAccess) => {
        this.setKeyboardStatus(isInstalled, hasFullAccess)
        if (isInstalled) {
          this.reportProgress('keyboardInstalled')
        }
        if (hasFullAccess) {
          this.reportProgress('hasFullAccess')
        }
      }
      window.shareButton = (id, instruction, category) => {
        this.receiveSharedButton(id)
        this.reportProgress('shareButton')
      }
      if (isIntelliKeyMacApp() || hasAppleInteropBridge()) {
        // Bridge readiness must not depend on a successful Firebase refresh.
        // In particular, keyboard login needs this callback to retry a failure.
        this.sendNativeMessage({ type: 'webReady' })
      }
    }
  }

  openKeyboardSettings = () => {
    if (hasNativeMessageBridge()) {
      const msg = {
        type: 'settings',
      }
      this.sendNativeMessage(msg)
    }
  }

  sendReferralCode = (code) => {
    if (hasNativeMessageBridge()) {
      const msg = {
        type: 'referralCode',
        referralCode: code,
        origin: window.location.origin
      }
      this.sendNativeMessage(msg)
    }
  }

  fullAccessRequested = false
  fullAccessRequestSubject = new Subject()
  observeFullAccessRequested = () => {
    return concat(of(this.fullAccessRequested, this.fullAccessRequestSubject))
  }

  keyboardStatusSubject = new Subject()

  hasFullAccess = () => this.keyboardStatus && this.keyboardStatus.hasFullAccess

  setKeyboardStatus = (isInstalled, hasFullAccess) => {
    if (hasFullAccess) {
      this.fullAccessRequested = false
    }
    this.keyboardStatus = {
      isInstalled,
      hasFullAccess
    }
    this.keyboardStatusSubject.next(this.keyboardStatus)
  }

  observeKeyboardStatus() {
    if (this.keyboardStatus) {
      return concat(of(this.keyboardStatus), this.keyboardStatusSubject)
    }
    return this.keyboardStatusSubject
  }

  macSetupStatus = null
  macSetupStatusSubject = new Subject()

  observeMacSetupStatus = () => {
    if (this.macSetupStatus) {
      return concat(of(this.macSetupStatus), this.macSetupStatusSubject)
    }
    return this.macSetupStatusSubject
  }

  performMacSetupAction = action => {
    if (!isIntelliKeyMacApp() || typeof action !== 'string') return false
    return this.sendNativeMessage({
      type: 'macSetupAction',
      action
    })
  }

  windowsSetupStatus = null
  windowsSetupStatusSubject = new Subject()

  observeWindowsSetupStatus = () => {
    if (this.windowsSetupStatus) {
      return concat(of(this.windowsSetupStatus), this.windowsSetupStatusSubject)
    }
    return this.windowsSetupStatusSubject
  }

  performWindowsSetupAction = action => {
    if (!isIntelliKeyWindowsApp() || typeof action !== 'string') return false
    return this.sendNativeMessage({
      type: 'windowsSetupAction',
      action
    })
  }

  nativeLogin = () => {
    if (hasNativeMessageBridge()) {
      const msg = {
        type: 'nativeLogin'
      }
      this.sendNativeMessage(msg)
    }
  }

  sendButtons = (buttons) => {
      if (hasNativeMessageBridge()) {
        const msg = {
          type: 'buttons',
          buttons: JSON.stringify({buttons})
        }
        this.sendNativeMessage(msg)
      }
  }

  sendReady = (token, authSnapshot = this.nativeAuthGeneration.snapshot()) => {
      if (hasNativeMessageBridge()) {
        const msg = {
          type: 'ready',
          ready: true,
          token,
          ...authSnapshot
        }
        this.sendNativeMessage(msg)
      }
  }

  sendCustomToken = async (token, authSnapshot = this.nativeAuthGeneration.snapshot()) => {
    if (hasNativeMessageBridge()) {
        const msg = {
          type: 'customToken',
          customToken: token,
          ...authSnapshot
        }
        this.sendNativeMessage(msg)
    } else {
      const ref = this.firebase.firestore().collection('UserChannel').doc(this.self.uid)
      if (window.electronAPI) {
        const { platform, osVersion, shell } = await this.electron('login', {
          customToken: token,
          model: 'gpt-4'
        })
        consoleLog("electron login", {platform, osVersion, shell })
        this.platform = platform
        this.osVersion = osVersion
        this.shell = shell
        await ref.set({
          platform, osVersion, shell
        }, { merge: true })
      } 
    }
  }

  sendActive = () => {
      if (hasNativeMessageBridge()) {
        const msg = {
          type: 'active',
          active: true,
        }
        this.sendNativeMessage(msg)
      }
  }

  customToken = null
  customTokenUid = null
  customTokenTimestamp = 0
  nativeIdentityPending = null

  synchronizeNativeIdentity = async () => {
    if (!hasNativeCapability('nativeIdentitySync')) return
    const snapshot = this.nativeAuthGeneration.snapshot()
    const isCurrent = () => this.nativeAuthGeneration.isCurrent(snapshot, this.firebase.auth().currentUser?.uid)
    if (!snapshot.authUID || !isCurrent()) throw new Error('The IntelliKey account changed. Please try again.')
    if (this.nativeIdentityPending?.generation === snapshot.authGeneration) return this.nativeIdentityPending.promise
    const promise = (async () => {
      let response = await this.nativeCall('synchronizeFirebaseIdentity', {}, 30000)
      if (!isCurrent()) throw new Error('The IntelliKey account changed. Please try again.')
      if (response?.needsCustomToken) {
        const customToken = await this.getCustomToken()
        if (!isCurrent()) throw new Error('The IntelliKey account changed. Please try again.')
        response = await this.nativeCall('synchronizeFirebaseIdentity', { customToken }, 30000)
      }
      if (!isCurrent() || response?.ok !== true || response.uid !== snapshot.authUID) {
        throw new Error(response?.error || 'Could not connect the IntelliKey account on this device. Please try again.')
      }
    })()
    this.nativeIdentityPending = { generation: snapshot.authGeneration, promise }
    try { await promise }
    finally { if (this.nativeIdentityPending?.promise === promise) this.nativeIdentityPending = null }
  }

  getIdToken = async () => {
    return await this.self.getIdToken(true)
  }
  
  getCustomToken = async () => {
    const session = this.nativeAuthGeneration.snapshot()
    this.assertCurrentAccount(session)
    const now = Date.now()
    const uid = session.authUID
    if (this.customTokenUid !== uid || now - this.customTokenTimestamp > 30 * 60 * 1000) {
      this.customTokenTimestamp = now
      this.customToken = null
      this.customTokenUid = uid
    }
    if (this.customToken) return this.customToken
    const headers = await requiredFirebaseAuthHeaders(() => this.getToken())
    this.assertCurrentAccount(session)
    const result = await axios.post(`${keyboardApiServer}/api/account/custom-token`, {}, {
      headers, timeout: 20000
    })
    this.assertCurrentAccount(session)
    const { customToken } = result.data
    if (!customToken || uid !== this.firebase.auth().currentUser?.uid) throw new Error('The IntelliKey account changed while connecting. Please try again.')
    this.customToken = customToken
    this.customTokenUid = uid
    return this.customToken
  }

  keyboardIsReady = async (keyboard) => {
    const wasReady = this.keyboard != null
    this.keyboard = keyboard
    if (this.isNative()) {
      this.sendActive()
    }
    if (wasReady) return
    if (!this.isNative()) {
      if (false) {
        const doc = "This is neat 😊😃"
        //const doc = '安特记住了威廉·莎士比亚的智慧格言：“过去的就是序幕！”'
        //const doc = "Ciao! Mi chiamo Bianca, e posso leggerti qualsiasi testo tu mi scriva."
        //window.edit(doc, doc.length - 30)
      } else {
        //window.edit("", 0)
      }
    }
  }

  keyboardDidClose = keyboard => {
    if (this.keyboard === keyboard) this.keyboard = null
  }

  cancelKeyboard = () => {
    const msg = {
    }
    if (this.isNative()) {
      this.sendNativeMessage(msg)
    }
    this.keyboardActive = false
    this.keyboardActiveSubject.next(false)
  }

  cancelKeyboardOutputForRequest = (
    requestId,
    document,
    authObservation = null
  ) => {
    const msg = {
      type: 'cancel',
    }
    if (isIntelliKeyWindowsApp()) {
      if (!matchesWindowsAuthObservation(
        this.nativeAuthGeneration,
        this.firebase.auth().currentUser?.uid,
        authObservation?.authGeneration,
        authObservation?.authUID
      )) {
        return false
      }
      const authenticated = attachWindowsAuthObservation(msg, authObservation)
      if (!authenticated) return false
      const exactRequestId = this.windowsWritingRequests.take(requestId)
      if (!exactRequestId) {
        return false
      }
      msg.requestId = exactRequestId
      Object.assign(msg, authenticated)
    }
    this.keyboardInput = null
    this.keyboardActive = false
    this.keyboardActiveSubject.next(false)
    if (this.isNative()) {
      this.sendNativeMessage(msg)
    }
    return true
  }

  cancelKeyboardOutput = document =>
    this.cancelKeyboardOutputForRequest(null, document)
  
  sendKeyboardOutputForRequest = (
    requestId,
    document,
    authObservation = null
  ) => {
    const msg = {
      type: 'document',
      document: JSON.stringify(document)
    }
    if (isIntelliKeyWindowsApp()) {
      if (!matchesWindowsAuthObservation(
        this.nativeAuthGeneration,
        this.firebase.auth().currentUser?.uid,
        authObservation?.authGeneration,
        authObservation?.authUID
      )) {
        return false
      }
      const authenticated = attachWindowsAuthObservation(msg, authObservation)
      if (!authenticated) return false
      const exactRequestId = this.windowsWritingRequests.take(requestId)
      if (!exactRequestId) {
        return false
      }
      msg.requestId = exactRequestId
      Object.assign(msg, authenticated)
    }
    //debugger
    this.keyboardInput = null
    this.keyboardActive = false
    this.keyboardActiveSubject.next(false)
    if (this.isNative()) {
      this.sendNativeMessage(msg)
    }
    return true
  }

  sendKeyboardOutput = document =>
    this.sendKeyboardOutputForRequest(null, document)

  keyboardSubject = new Subject()

  observeKeyboard = () => {
    if (this.keyboardInput) {
      let value = this.keyboardInput
      this.keyboardInput = null
      return concat(of(value), this.keyboardSubject)
    }
    return this.keyboardSubject
  }

  keyboardActive = false
  keyboardActiveSubject = new Subject()

  observeKeyboardIsActive() {
    return concat(of(this.keyboardActive), this.keyboardActiveSubject)
  }
  
  constructor (firebase, config) {
    this.firebase = firebase
    this.config = config
    this.tokenBilling = new TokenBilling({
      supported: () => hasNativeCapability('tokenPackSandbox'),
      getSession: () => ({
        ...this.nativeAuthGeneration.snapshot(),
        providerKey: [(this.firebase.auth().currentUser?.providerData || [])
          .map(provider => provider.providerId).sort().join(','),
          (this.accountLinks?.members || [this.firebase.auth().currentUser?.uid || '']).slice().sort().join(',')].join(':')
      }),
      isCurrent: snapshot => this.nativeAuthGeneration.isCurrent(snapshot, this.firebase.auth().currentUser?.uid),
      synchronizeIdentity: () => this.synchronizeNativeIdentity(),
      nativeCall: (...args) => this.nativeCall(...args)
    })
    this.tokenPurchases = new TokenPurchaseClient({
      api: (path, input) => this.purchaseApi(path, input),
      nativeCall: (...args) => this.nativeCall(...args),
      getSession: () => this.nativeAuthGeneration.snapshot(),
      isCurrent: snapshot => this.nativeAuthGeneration.isCurrent(snapshot, this.firebase.auth().currentUser?.uid),
      hasApplePurchases: () => hasAppleInteropBridge() && hasNativeCapability('tokenPurchases'),
      isNative: () => this.isNative(),
      synchronizeIdentity: () => this.synchronizeNativeIdentity(),
      prepareBilling: () => this.tokenBilling.ensure()
    })
    const auth = this.firebase.auth();
    auth.onAuthStateChanged(this.onAuthStateChanged);
    this.onAuthStateChanged(auth.currentUser);
    this.config = config
    // WebView2 has a standards-compliant window.postMessage implementation
    // that the hosted app must not replace. New native shells use this narrow
    // callback; Apple legacy shells retain their existing callback contract.
    window.__INTELLIKEY_RECEIVE_NATIVE_MESSAGE__ = this.onNativeMessage
    if (!isIntelliKeyWindowsApp()) {
      window.postMessage = this.onNativeMessage
    }
    window.observeContactOnline = this.observeContactOnline
    this.linkPreviewer = new LinkPreviewer(
      firebase,
      this.getFunc('api/link-preview')
    );
    this.nativeInit()
    window.blockInput = () => {
    }
    window.unblockInput = () => {
    }
    if (Token) {
      this.firebase.auth().signInWithCustomToken(Token)
    }
    window.addEventListener('resize', this.applyOrientation)
    this.applyOrientation()
    this.connectivitySub = this.observeServerConnectivity().subscribe(isReachable => {
      consoleLog("server is reachable", isReachable)
    })
    if (this.isAskAppOnly()) {
      //debugger
      consoleLog("signing in anonymously")
      this.firebase.auth().signInAnonymously()
    }
  }

  applyOrientation = () => {
    if (window.innerHeight > window.innerWidth) {
      this.orient = 'portrait'
    } else {
      this.orient = 'landscape'
    }
    this.orientSubject.next(this.orient)
  }

  orientSubject = new Subject()
  observeOrientation() {
    if (this.orient) {
      return concat(of(this.orient), this.orientSubject)
    }
    return this.orientSubject
  }


  shareMove = async move => {
    const url = await this.getVideoURL(move)
    this.sendNativeMessage({
      type: 'shareFile',
      id: move.getName(),
      url
    })
  }

  getVideoURL = async move => {
    const id = move.getDatabaseId()
    const filename = `Moves/${this.self.uid}/${id}/Video.mp4`
    const storage = this.firebase.storage()
    const ref = storage.ref(filename)
    return await ref.getDownloadURL()
  }

  getMoveId = move => {
    const compileTracks = (move, tracks) => {
      if (move.moves) {
	move.moves.map(x => compileTracks(x, tracks))
      } else {
	tracks.push(move)
      }
    }
    const tracks = []
    compileTracks(move, tracks)
    const id = tracks.map(track => track.move.id).join('+')
    return id
  }

  getComboMoveVideoURL = async move => {
    const id = this.getMoveId(move)
    const filename = `Moves/Rendered/${id}/Video.mp4`
    const storage = this.firebase.storage()
    const ref = storage.ref(filename)
    return await ref.getDownloadURL()
  }

  providerTypeFilter = providerType => specialty => filterSpecialty(specialty, providerType)

  observeCustomer = () => {
    return doc(this.firebase.firestore().collection('Customers').doc(this.self.uid)).pipe(map(snap => {
      return snap.exists ? snap.data() : null
    }))
  }

  observePaymentMethods = () => {
    return this.observeCustomer().pipe(map(customer => {
      if (customer) {
        let { paymentMethods, selectedPaymentMethod } = customer
        if (paymentMethods && selectedPaymentMethod) {
          selectedPaymentMethod = paymentMethods.find(x => x.id === selectedPaymentMethod)
        }
        return { paymentMethods, selectedPaymentMethod }
      }
      return {}
    }))
  }

  reqs = []
  callId = 0
  nativeCall = (type, data, timeoutMs = 5000) => {
    const id = ++this.callId
    const authObservation = this.nativeAuthGeneration.snapshot()
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        delete this.reqs[id]
        reject(new Error(`Native call timed out: ${type}`))
      }, timeoutMs)
      this.reqs[id] = {
        authObservation,
        reject: error => {
          clearTimeout(timer)
          reject(error)
        },
        resolve: response => {
          clearTimeout(timer)
          resolve(response)
        }
      }
      const call = {
        type,
      }
      call[type] = data
      this.sendNativeMessage({
        type: 'call',
        reqId: id,
        call,
        ...authObservation
      })
    })
  }

  getCurrentLocation = () => {
    if (typeof window !== 'undefined' && window.ReactNativeWebView) {
      return this.nativeCall({
        type: 'location'
      }).then(response => {
        return response.coords
      })
    }
    return getCurrentPosition()
  }

  observeMoves = () => {
    return from([])
  }

  testServerEdit = async () => {
    const result = await this.edit("add emojis", "")
    consoleLog(result)
    //debugger
  }

  searchMusic = async term => {
    const func = this.getFunc('api/music/search')
    const result = await func({term})
    consoleLog(JSON.stringify(result, null, ' '))
    return result.data.results
  }

  getOnsets = async song => {
    const func = this.getFunc('api/music/onsets')
    const result = await func({song})
    const q = this.firebase.firestore().collection('Onsets').where('id', '==', String(song.id))
    const { docs } = await q.get()
    return docs[0].data()
  }

  observeBaseMoves = () => {
    return from([])
  }
  
  observeBaseMovesFromDb = () => {
    return from([])
  }

  canSaveMove = move => {
    const result = move.getOwner() === this.self.uid
    //debugger
    return result
  }

  observeMyMoves = (uid) => {
    return from([])
  }

  getBaseTools = async () => {
    this.baseTools = {}
    const func = this.getFunc("getBaseTools")
    const response = await func({})
    const { tools } = response.data
    for (const { tool } of tools) {
      try {
        this.baseTools[tool.function.name] = tool
        this.toolsSubject.next({ type: 'added', tool })
      } catch (err) {
        console.error(err)
      }
    }
    return this.baseTools
  }


  toolsSubject = new Subject()

  observeTools = () => {
    if (!this.baseTools) {
      this.baseTools = {}
      this.getBaseTools()
    }
    const events = Object.values(this.baseTools).map(tool => { type: 'added', tool })
    return concat(from(events), this.toolsSubject)
  }

  observeAllMoves = () => {
    return mergeN(this.observeBaseMoves(), this.observeMyMoves())
  }

  saveAs = async (blob, filename) => {
    if (window.ReactNativeWebView) {
      const storage = this.firebase.storage()
      const ref = storage.ref(`Uploads/${this.self.uid}/${filename}`)
      await ref.put(blob)
      const url = await ref.getDownloadURL()
      await this.nativeCall('downloadFile', {
        url: url,
        name: filename,
        mimeType: blob.type
      })
      await ref.delete()
    } else {
      saveAs(blob, filename)
    }
  }

  download = async (move, filename) => {
    consoleLog("downloading move", move)
    const id = move.getDatabaseId()
    const uid = move.getOwner()
    let path
    if (true || !id) {
      consoleLog('calling renderMove')
      const func = this.getFunc('api/moves/render')
      const result = await func( { move: move.toJSON() } )
      const { storagePath } = result.data
      consoleLog('renderMove storagePath', storagePath)
      path = storagePath
    } else {
      path = `Moves/${this.self.uid}/${id}/Video.mp4`
    }
    consoleLog("path", path)
    const ref = this.firebase.storage().ref(path)
    const url = await ref.getDownloadURL()
    consoleLog("got download url", url)
    if (this.isNative()) {
      await this.nativeCall('downloadFile', {
        url,
        name: 'Video.mp4',
        mimeType: 'video/mp4'
      })
    } else {
      saveAs(url, filename)
    }
  }

  deleteMove = async move => {
    const c = this.firebase.firestore().collection('MyMoves')
    const id = move.getDatabaseId()
    const ref = c.doc(id)
    return await ref.delete()
  }

  saveMove = async move => {
    const c = this.firebase.firestore().collection('MyMoves')
    const id = move.getDatabaseId()
    let ref
    if (!id) {
      ref = c.doc()
    } else {
      ref = c.doc(id)
    }
    const json = move.toJSON()
    //debugger
    const data = {
      uid: this.self.uid,
      move: json,
      lastUpdated: Date.now()
    }
    await ref.set(data)
    return ref.id
  }

  getLocation = async opts => {
    if (!opts) return this.getCurrentLocation()
    const { latitude, longitude, address } = opts
    consoleLog(opts)
    //debugger
    const func = this.getFunc('api/location/resolve')
    const result = await func({
      lat: latitude,
      lng: longitude,
      address
    })
    const { results } = result.data
    const { geometry, address_components } = results[0]
    const streetNumber = address_components.find(x => x.types.find(y => y == 'street_number')).short_name
    const street = address_components.find(x => x.types.find(y => y == 'route')).short_name
    let suite = ''
    const subPremise = address_components.find(x => x.types.find(y => y == 'subpremise'))
    if (subPremise) {
      suite = `, ${subPremise.short_name}`
    }
    let lat, lng
    if (geometry) {
      lat = geometry.location.lat
      lng = geometry.location.lng
    }
    return {
      latitude: lat,
      longitude: lng,
      address: `${streetNumber} ${street}${suite}`,
      address_components
    }
  }
  
  onNativeMessage = json => {
    //this.nativeLog('onNativeMessage: ' + json)
    //if (json.source) return
    let msg
    try {
      msg = JSON.parse(json)
    } catch {
      this.nativeLog('JSON.parse failed')
      return
    }
    if (!msg || typeof msg !== 'object' || Array.isArray(msg)) return
    if (isIntelliKeyWindowsApp() && !matchesWindowsAuthObservation(
      this.nativeAuthGeneration,
      this.firebase.auth().currentUser?.uid,
      msg.authGeneration,
      msg.authUID
    )) {
      if (msg.type === 'response') {
        const pending = this.reqs[msg.reqId]
        delete this.reqs[msg.reqId]
        pending?.reject(new Error('Native call response was stale.'))
      }
      return
    }
    consoleLog('onNativeMessage', msg.type)
    if (msg.type === 'applePurchaseTransaction') {
      this.tokenPurchases.verify(msg.transaction).catch(() => {
        this.nativeLog('Apple purchase remains unfinished; it will be retried for its owner.')
      })
    } else if (msg.type === 'privateInferenceChunk') {
      const pending = this.privateStreams.get(msg.id)
      const current = this.nativeAuthGeneration.snapshot()
      if (pending && msg.authUID === this.firebase.auth().currentUser?.uid &&
          pending.authObservation.authUID === current.authUID &&
          pending.authObservation.authGeneration === current.authGeneration && typeof msg.text === 'string') {
        pending.onContent(msg.text)
      }
    } else if (msg.type === 'token') {
      this.saveToken(msg.token)
    } else if (msg.type === 'notification') {
      //this.nativeLog("received not: " + msg.notification.data.type)
      this.notificationSubject.next(msg.notification)
    } else if (msg.type === 'safeArea') {
      window.safeAreaInsets = msg.safeArea
      //this.nativeLog("window.safeAreaInsets=>"+window.safeAreaInsets);
    } else if (msg.type === 'url') {
      //alert("initial url: " + msg.url)
      this.url = msg.url
      this.urlSubject.next(this.url)
    } else if (msg.type === 'openWritingAssistantDictation') {
      this.requestPage(WRITING_ASSISTANT_DICTATION_PAGE)
    } else if (msg.type === 'focusWritingAssistantAtEnd') {
      focusOrOpenWritingAssistantAtEnd({
        keyboard: this.keyboard,
        requestPage: this.requestPage
      })
    } else if (msg.type === 'writingAssistantVoiceInputCommand') {
      if (typeof msg.action === 'string') {
        this.writingAssistantVoiceInputCommandSubject.next(msg.action)
      }
    } else if (msg.type === 'creds') {
      this.creds = msg
      this.credsSubject.next(this.creds)
    } else if (msg.type === 'openAICodexOAuthCallback') {
      this.completeOpenAIOAuthLogin(msg.url || msg.redirectUrl || msg.code).catch(() => {
        console.error('openAICodexOAuthCallback failed')
        this.nativeLog('OpenAI OAuth callback failed')
      })
    } else if (msg.type === 'macSetupStatus') {
      const status = normalizeMacSetupStatus(msg)
      if (status) {
        this.macSetupStatus = status
        this.macSetupStatusSubject.next(status)
      }
    } else if (msg.type === 'windowsSetupStatus') {
      const status = normalizeWindowsSetupStatus(msg)
      if (status) {
        this.windowsSetupStatus = status
        this.windowsSetupStatusSubject.next(status)
      }
    } else if (msg.type === 'response') {
      const pending = this.reqs[msg.reqId]
      if (pending) {
        delete this.reqs[msg.reqId]
        if (!this.nativeAuthGeneration.isCurrent(pending.authObservation, this.firebase.auth().currentUser?.uid)) {
          pending.reject(new Error('The IntelliKey account changed during the native call.'))
          return
        }
        if (isIntelliKeyWindowsApp() && !matchesWindowsNativeReply(
          this.nativeAuthGeneration,
          this.firebase.auth().currentUser?.uid,
          pending.authObservation,
          msg
        )) {
          pending.reject(new Error('Native call response did not match its request.'))
          return
        }
        pending.resolve(msg.response)
      }
    }
  }

  utcOffset = -(new Date().getTimezoneOffset()*60*1000)

  saveToken = async token => {
    try {
      const func = this.getFunc('api/notifications/token')
      const result = await func({
        token: token,
        utcOffset: this.utcOffset,
      })
      debugLog(result)
    } catch (err) {
      console.error(err)
    }
  }
  
  credsSubject = new Subject()
  
  observeCreds = () => {
    if (this.creds) return concat(of(this.creds), this.credsSubject)
    return this.credsSubject
  }

  setStatusBarColor = color => {
    consoleLog('set status bar color:', color)
    this.sendNativeMessage({
      type: 'statusBarColor',
      color: color 
    })
  }

  selfSubject = new Subject()

  observeSelf = () => {
    const existing = this.self ? [this.self] : []
    return concat(existing, this.selfSubject)
  }

  getIOSAppStoreReceipt = () => {
    if (hasNativeCapability('tokenPurchases')) {
      return this.tokenPurchases.resume().catch(() => this.nativeLog('Purchase recovery will retry when Token Packs opens.'))
    }
    const reqId = String(++this.reqId)
    const msg = {
      type: 'receipt',
      reqId
    }
    consoleLog(msg)
    if (hasAppleInteropBridge()) {
      window.webkit.messageHandlers.interOp.postMessage(JSON.stringify(msg))
    }
  }

  sendKeyboardSettings = (settings) => {
    this.keyboardSettings = normalizeKeyboardSettings(settings)
    const {
      hapticFeedback,
      audioFeedback 
    } = this.keyboardSettings
    if (this.isNative()) {
      const level = effectiveAutocorrectionLevel(this.keyboardSettings)
      const msg = {
        type: 'keyboardSettings',
        autoCorrection: level > 1,
        autoCorrectionLevel: level,
        hapticFeedback,
        audioFeedback
      }
      this.sendNativeMessage(msg)
    }
  }

  onAuthStateChanged = user => {
    const authSnapshot = this.nativeAuthGeneration.transitionToUID(user?.uid)
    if (user && this.self && user.uid === this.self.uid) {
      this.self = user
      return
    }
    const isCurrent = () => this.nativeAuthGeneration.isCurrent(authSnapshot, this.firebase.auth().currentUser?.uid)
    this.profileSub?.unsubscribe()
    this.profileSub = null
    this.platformSub?.unsubscribe()
    this.platformSub = null
    this.profile = null
    this.isAdmin = false
    this.isReview = false
    this.keyboardSettings = normalizeKeyboardSettings()
    this.referralCode = undefined
    this.apiKey = undefined
    this.self = user
    this.accountLinksSub?.unsubscribe()
    this.accountLinks = null
    if (user) this.accountLinksSub = this.observeAccountScope().subscribe({
      next: scope => {
        if (!isCurrent()) return
        const changed = JSON.stringify(this.accountLinks?.members) !== JSON.stringify(scope.members)
        this.accountLinks = scope
        if (changed && scope.members.length > 1) this.tokenBilling.ensure().catch(() => {})
      },
      error: () => { if (isCurrent()) this.accountLinks = null }
    })
    consoleLog('self', user)
    this.selfSubject.next(user)

    if (this.self) {
      this.firebase.firestore().collection('Admin').where('uid', '==', this.self.uid).get().then( ({ docs }) => {
        if (isCurrent()) this.isAdmin = docs.length > 0
      }).catch(ignored => {
      })
      this.firebase.firestore().collection('Review').where('uid', '==', this.self.uid).get().then( ({ docs }) => {
        if (isCurrent()) this.isReview = docs.length > 0
      }).catch(ignored => {
      })
      this.profileSub = this.observeProfile().subscribe(profile => {
        if (isCurrent()) this.profile = profile
      })
      this.getAllButtons().then(({buttons}) => {
        //debugger
        if (isCurrent() && buttons) {
          this.sendButtons(buttons)
        }        
      }).catch(() => {})
      if (this.isHelpful()) {
        //debugger
        if (this.platformSub) {
          this.platformSub.unsubscribe()
        }
        const q = this.firebase.firestore().collection('UserChannel').doc(this.self.uid)
        this.platformSub = docData(q).subscribe(data => {
          if (isCurrent() && data) {
            const { platform, osVersion, shell } = data
            if (platform) {
              this.platform = platform
              this.osVersion = osVersion
              this.shell = shell
            }
          }
        })
      }
      if (this.isNative()) {
        this.getIdToken().then(token => {
          if (this.nativeAuthGeneration.isCurrent(
            authSnapshot,
            this.firebase.auth().currentUser?.uid
          )) {
            this.sendReady(token, authSnapshot)
          }
        }).catch(() => this.nativeLog('Keyboard token refresh failed; keyboard login can retry.'))
        if (hasNativeCapability('nativeIdentitySync')) {
          this.synchronizeNativeIdentity().catch(() => this.nativeLog('Native account handoff failed; subscription operations will retry.'))
        } else this.getCustomToken().then(token => {
          if (this.nativeAuthGeneration.isCurrent(
            authSnapshot,
            this.firebase.auth().currentUser?.uid
          )) {
            return this.sendCustomToken(token, authSnapshot)
          }
        }).catch(() => this.nativeLog('Native account handoff failed.'))
        this.getReferralCode().then(code => {
          if (isCurrent()) this.sendReferralCode(code)
        }).catch(() => {})
        this.getKeyboardSettings().then(keyboardSettings => {
          if (this.nativeAuthGeneration.isCurrent(authSnapshot, this.firebase.auth().currentUser?.uid)) {
            this.sendKeyboardSettings(keyboardSettings)
          }
        }).catch(() => {})
        this.getIOSAppStoreReceipt()    
      } else {
        this.getReferralCode().then(code => {
          //debugger
          if (isCurrent()) this.sendReferralCode(code)
        }).catch(() => {})
        this.keyboardIsReady()
      }
      this.getAzureVoices()
      const myURL = new URL(window.location.href)
      if (myURL.pathname === '/shareButton') {
        const params = new URLSearchParams(window.location.search)
        const buttonId = params.get('id')
        if (buttonId) {
          this.receiveSharedButton(buttonId)
        }
      } else if (myURL.pathname === '/shareThread') {
        const params = new URLSearchParams(window.location.search)
        const threadId = params.get('id')
        const from = params.get('from')
        if (from && threadId) {
          this.receiveSharedThread(from, threadId)
        }
      }
    } else {
      if (this.profileSub) {
        this.profileSub.unsubscribe()
        this.profileSub = null
      }
      if (this.isNative()) {
        // Publish the exact signed-out generation even on initial load. This
        // prevents a restored native account from remaining eligible when the
        // visible WebView has no Firebase user, and gives native Google sign-in
        // a versioned state to bind its staged transaction to.
        this.sendNativeMessage({ type: 'signOut', ...authSnapshot })
      }
    }
  }

  getAzureVoices = async () => {
    this.azureVoices = null
    return []
  }

  getGoogleVoices = async () => {
    this.googleVoices = []
    return this.googleVoices
  }

  emailExists = async email => {
    email = email.trim().toLowerCase()
    const func = this.getPublicFunc('api/account/email-exists')
    const result = await func({email})
    return result.data
  }

  phoneNumberExists = async phoneNumber => {
    const func = this.getPublicFunc('api/account/phone-number-exists')
    const result = await func({phoneNumber})
    return result.data
  }

  verifyInvite = async (email, verificationCode) => {
    const func = this.getFunc('api/invitations/verify')
    const arg = { email, verificationCode: Number(verificationCode) }
    const result = await func(arg)
    consoleLog('verifyInvite', arg, result)
    return result.data
  }

  createAccount = async (email, password) => {
    const result = await this.firebase.auth().createUserWithEmailAndPassword(email, password)
    return result.user
  }

  createCustomerAccount = async (verification, form) => {
    consoleLog('createCustomerAccount', verification, form)
    const { name, email, phoneNumber, address, country, state, zip, password } = form
    let user
    if (!this.self) {
      user = await this.createAccount(email, password)
    } else {
      user = this.self
    }
    const func = this.getFunc('api/account/customer/setup')
    const { verificationCode } = verification
    const arg = {
      invite: { verificationCode: parseInt(verificationCode), email },
      form: {
        name, email, phoneNumber, address, country, state, zip
      }
    }
    consoleLog(arg)
    //debugger
    const result = await func(arg)
    consoleLog(result)
    //debugger
    if (result.error) {
      throw new Error(result.error)
    }
  }    
  
  signIn = async (email, password) => {
    email = email.trim()
    password = password.trim()
    consoleLog("signin in")
    const result = await this.firebase.auth().signInWithEmailAndPassword(email, password)
    this.onAuthStateChanged(result.user)
    const creds = {
      type: 'login',
      email: email,
      password: password,
      phoneNumber: result.user.phoneNumber
    }
    //alert('login ' + JSON.stringify(creds))
    this.sendNativeMessage(creds)
  }

  signInWithGoogle = async noLink => {
    const auth = this.firebase.auth()
    const linkUser = noLink ? null : auth.currentUser
    const hasNativeIdentity = hasNativeIdentityBridge()
    const completeNativeGoogle = async request => {
      if (!isIntelliKeyWindowsApp()) {
        return this.nativeCall('completeGoogleSignIn', request, 30 * 1000)
      }

      const currentUser = auth.currentUser
      if (!currentUser || currentUser.uid !== request.uid) {
        throw new Error('The signed-in IntelliKey account changed before native completion.')
      }
      const webIdToken = await currentUser.getIdToken(true)
      // signInWithCustomToken may resolve before Firebase's observer callback.
      // Advance the shared Web generation synchronously so the attested token
      // and native Complete call always describe the same resolved UID.
      this.nativeAuthGeneration.transitionToUID(currentUser.uid)
      const attestedRequest = { ...request, webIdToken }
      try {
        return await this.nativeCall(
          'completeGoogleSignIn',
          attestedRequest,
          15 * 1000
        )
      } catch (ignored) {
        // The first call may have committed durably while its WebView reply
        // was lost. Retry the exact transaction once; Windows completion is
        // idempotent only while its short-lived durable outcome is current.
        return await this.nativeCall(
          'completeGoogleSignIn',
          attestedRequest,
          15 * 1000
        )
      }
    }
    const abortNativeGoogle = async request => {
      if (!isIntelliKeyWindowsApp()) {
        return this.nativeCall('abortGoogleSignIn', request, 30 * 1000)
      }
      try {
        return await this.nativeCall(
          'abortGoogleSignIn',
          request,
          15 * 1000
        )
      } catch (ignored) {
        return await this.nativeCall(
          'abortGoogleSignIn',
          request,
          15 * 1000
        )
      }
    }
    return authenticateWithGoogle({
      firebase: this.firebase,
      linkUser,
      mergeAccount: this.mergeAccount,
      nativeSignIn: hasNativeIdentity
        ? request => this.nativeCall('signInWithGoogle', request, 5 * 60 * 1000)
        : null,
      nativeComplete: hasNativeIdentity
        ? completeNativeGoogle
        : null,
      nativeAbort: hasNativeIdentity
        ? abortNativeGoogle
        : null
    })
  }

  canSignInWithApple = canSignInWithApple

  signInWithApple = async noLink => {
    if (!canSignInWithApple()) return { error: new Error('Update IntelliKey to use Apple sign-in.') }
    const auth = this.firebase.auth()
    const linkUser = noLink ? null : auth.currentUser
    const native = hasNativeMessageBridge()
    if (!noLink && !linkUser) return { error: new Error('Sign in before linking your Apple account.') }
    if (native && linkUser) {
      await this.synchronizeNativeIdentity()
      if (auth.currentUser?.uid !== linkUser.uid) return { error: new Error('The IntelliKey account changed.') }
    }
    const result = await authenticateWithApple({
      firebase: this.firebase,
      linkUser,
      mergeAccount: this.mergeAccount,
      nativeSignIn: native ? request => this.nativeCall('signInWithApple', request, 5 * 60 * 1000) : null,
      nativeComplete: native ? request => this.nativeCall('completeAppleSignIn', request, 30000) : null,
      nativeAbort: native ? request => this.nativeCall('abortAppleSignIn', request, 30000) : null
    })
    if (result?.user && linkUser) {
      // Native linking also upgrades an anonymous Firebase account. Reload the
      // Web user so its provider list and isAnonymous state match that upgrade.
      const user = await this.refreshCurrentUser()
      if (user?.uid !== linkUser.uid) return { error: new Error('The IntelliKey account changed.') }
      return { ...result, user }
    }
    return result
  }

  isSignedInAnonymously = () => {
    const user = this.firebase.auth().currentUser
    const result = user && user.isAnonymous && !(this.accountLinks?.members.length > 1)
    consoleLog("isAnonymous", result)
    return result
  }

  signInAnonymously = async () => {
    return await this.firebase.auth().signInAnonymously()
  }

  signInWithPhoneNumber = async (phoneNumber, recaptcha) => {
    const { exists }  = await this.phoneNumberExists(phoneNumber)
    if (!exists && await this.isSignedInAnonymously()) {
      return await this.firebase.auth().currentUser.linkWithPhoneNumber(phoneNumber, recaptcha)
    }
    return await this.firebase.auth().signInWithPhoneNumber(phoneNumber, recaptcha)
  }

  linkPhoneNumber = async (phoneNumber, recaptcha) => {
    return await verifyAndMergePhone({
      firebase: this.firebase,
      phoneNumber,
      appVerifier: recaptcha,
      mergeAccount: this.mergeAccount
    })
  }

  mergeAccount = async (sourceIdToken, expectedUID) => {
    const session = this.nativeAuthGeneration.snapshot()
    const user = this.firebase.auth().currentUser
    if (!user || user.uid !== expectedUID) throw new Error('The IntelliKey account changed.')
    const token = await user.getIdToken(true)
    this.assertCurrentAccount(session)
    if (this.firebase.auth().currentUser?.uid !== expectedUID) throw new Error('The IntelliKey account changed.')
    let data
    try {
      data = (await axios.post(`${keyboardApiServer}/api/account/merge`, { sourceIdToken }, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 30000
      })).data
    } catch {
      // Do not expose an Axios error containing either account's credential.
      throw new Error('Could not finish linking the account. Please try again.')
    }
    this.assertCurrentAccount(session)
    if (this.firebase.auth().currentUser?.uid !== expectedUID) throw new Error('The IntelliKey account changed.')
    return data
  }

  getAccountLinks = async () => {
    const session = this.nativeAuthGeneration.snapshot()
    const uid = this.firebase.auth().currentUser?.uid
    const headers = await this.requiredAuthHeaders()
    this.assertCurrentAccount(session)
    const { data } = await axios.post(`${keyboardApiServer}/api/account/links`, {}, { headers, timeout: 30000 })
    this.assertCurrentAccount(session)
    if (this.firebase.auth().currentUser?.uid !== uid) throw new Error('The IntelliKey account changed.')
    return data
  }

  observeAccountScope = () => {
    const uid = this.self?.uid
    if (!uid) return of({ accountUID: null, members: [] })
    return doc(this.firebase.firestore().collection('AccountLinks').doc(uid)).pipe(map(snapshot => {
      const data = snapshot.exists ? snapshot.data() : { accountUID: uid, members: [uid] }
      if (!data.members?.includes(uid) || data.status === 'deleted') throw new Error('Account unavailable.')
      return data
    }), catchError(error => {
      if (error.code !== 'permission-denied') throw error
      return defer(() => this.legacyAccountScope(uid))
    }))
  }

  legacyAccountScope = async uid => {
    try { return await this.getAccountLinks() } catch (error) {
      // A pre-merge server has no directory route. Keep its existing singleton
      // behavior during rollout; never treat auth/network errors as membership.
      if (error.response?.status !== 404 || this.self?.uid !== uid) throw error
      return { accountUID: uid, members: [uid] }
    }
  }

  getAccountScope = async () => {
    const uid = this.self?.uid
    let snapshot
    try { snapshot = await this.firebase.firestore().collection('AccountLinks').doc(uid).get() }
    catch (error) {
      if (error.code !== 'permission-denied') throw error
      return this.legacyAccountScope(uid)
    }
    if (this.self?.uid !== uid) throw new Error('The IntelliKey account changed.')
    return snapshot.exists ? snapshot.data() : { accountUID: uid, members: [uid] }
  }

  refreshCurrentUser = async () => {
    const auth = this.firebase.auth()
    const user = auth.currentUser
    if (!user?.uid) return null

    const expectedUID = user.uid
    await user.reload()
    const refreshedUser = auth.currentUser
    if (!refreshedUser || refreshedUser.uid !== expectedUID) {
      const error = new Error('The signed-in IntelliKey account changed while refreshing account details.')
      error.code = 'auth/user-changed'
      throw error
    }

    this.self = refreshedUser
    this.selfSubject.next(refreshedUser)
    return refreshedUser
  }

  updatePassword = async newPassword => {
    await this.firebase.auth().currentUser.updatePassword(newPassword)
  }

  resetPassword = async email => {
    await this.firebase.auth().sendPasswordResetEmail(email);
  }

  signOut = async () => {
    const uid = this.firebase.auth().currentUser?.uid
    if (hasNativeCapability('clearSubscriptionsOnSignOut')) {
      const result = await this.nativeCall('clearLocalSubscriptions', {}, 45000)
      if (result?.ok !== true) {
        throw new Error(result?.error || 'Could not clear provider connections on this device. Please try again.')
      }
    }
    if (this.firebase.auth().currentUser?.uid !== uid) {
      throw new Error('The account changed during sign-out. Please try again.')
    }
    this.signUpDisplayName = null;
    this.creds = null
    const authSnapshot = this.nativeAuthGeneration.beginSignOut()
    this.customToken = null
    this.customTokenUid = null
    this.customTokenTimestamp = 0
    this.sendNativeMessage({ type: 'signOut', explicit: true, ...authSnapshot })
    await this.firebase.auth().signOut()
  }

  likeCache = {}

  likes = async (id) => {
    const docId = id + '-' + this.self.uid
    if (this.likeCache[docId] === undefined) {
      const snap = await this.firebase.firestore().collection('Liked').doc(docId).get()
      this.likeCache[docId] = snap.exists
    }
    return this.likeCache[docId]
  }

  saveComment = async (id, msg) => {
    const func = this.getFunc('api/comments/save')
    const { ts, from, to, text } = msg
    const response = await func({ id, msg: { ts, from, to, text } })
  }

  deleteComment = async (id, msg) => {
    const ref = this.firebase.firestore().collection('MyMoves').doc(id).collection('Comments').doc(String(msg.ts))
    await ref.delete()
  }
  
  observeComments = sharedMove => {
    const q = this.firebase.firestore().collection('MyMoves').doc(sharedMove.id).collection('Comments')
    return collectionChanges(q).pipe(flatMap(changes => {
      return changes.map(change => {
	const { type, doc } = change
	const data = doc.data()
	return {
	  type,
	  comment: data
	}
      })
    }))
  }

  resolveUserAsync = async uid => {
    const user = this.resolveUser(uid)
    if (user) return user
    const snap = await this.firebase.firestore().collection('Users').doc(uid).get()
    return snap.data()
  }

  observeMoveStatus = move => {
    return from([])
  }

  observeMove = id => {
    return from([])
  }

  saveReaction = async (id, ts, emoji) => {
    const func = this.getFunc('api/comments/react')
    const response = await func({
      id, ts, emoji
    })
  }

  like = async id => {
    const func = this.getFunc('api/moves/like')
    const response = await func({id})
    const { likes } = response.data
    const docId = id + '-' + this.self.uid
    this.likeCache[docId] = likes
    return likes
  }

  follow = async uid => {
    const func = this.getFunc('api/accounts/follow')
    const response = await func({uid})
    return response.data
  }
  
  unfollow = async uid => {
    const func = this.getFunc('api/accounts/unfollow')
    const response = await func({uid})
    return response.data
  }

  observeFollowing = () => {
    const q = this.firebase.firestore().collection('Users').doc(this.self.uid).collection('Following')
    return collectionChanges(q).pipe(flatMap(changes => {
      return changes.map(change => {
	const { type, doc } = change
	const data = doc.data()
	data.id = doc.id
	return {
	  type,
	  user: data
	}
      })
    }))
  }

  observeFollowers = () => {
    const q = this.firebase.firestore().collection('Users').doc(this.self.uid).collection('Followers')
    return collectionChanges(q).pipe(flatMap(changes => {
      return changes.map(change => {
	const { type, doc } = change
	const data = doc.data()
	data.id = doc.id
	return {
	  type,
	  user: data
	}
      })
    }))
  }

  userCache = {}

  observeUsers = (selection) =>  {
    let c = this.firebase.firestore().collection('Users')
    return collectionChanges(c).pipe(flatMap(changes => {
      return changes.map(change => {
	const { type, doc } = change
	const data = doc.data()
        if (type === 'removed') {
          delete this.userCache[doc.id]
        } else {
          this.userCache[doc.id] = data
        }
	data.id = doc.id
	return {
	  type,
	  user: data
	}
      })
    }))
  }

  observeContactOnline = () => {
    return from([])
  }

  resolveUser = uid => {
    if (uid === this.self.uid) {
      this.profile
    }
    return this.userCache[uid]
  }
  
  observeProfile = () => {
    return of(this.self)
  }

  observeMyMovesRaw = (timestamp) => {
    const c = this.firebase.firestore().collection('MyMoves')
    const q = c.where('uid', '==', this.self.uid).where('lastUpdated', '>=', timestamp).orderBy('lastUpdated', 'asc')
    return collectionChanges(q).pipe(flatMap(changes => {
      return changes.map(change => {
        const { type, doc } = change
        const id = doc.id
        const data = doc.data()
        data.id = id
        return {
	  type,
	  data
        }
        
      })
    }))
  }
  
  observeHome = (timestamp) => {
    return this.observeForYou(timestamp)
  }

  observeForYou = (timestamp) => {
    return from([])
  }

  observeChatSettings = () => {
    return this.observeSelf().pipe(flatMap(self => {
      return this.observeAccountScope().pipe(switchMap(account => doc(this.firebase.firestore().collection('ChatSettings').doc(account.accountUID))), map(snap => {
          if (snap.exists) {
            return snap.data()
          } 
          return {
            model: "gpt-3.5-turbo"
          }
        }))
    }))
  }

  observeSettings = () => {
    return this.observeSelf().pipe(switchMap(self => {
      //debugger
      if (self) {
        return this.observeAccountScope().pipe(switchMap(account => doc(this.firebase.firestore().collection('KeyboardSettings').doc(account.accountUID))), map(snap => {
          const settings = normalizeKeyboardSettings(snap.exists ? snap.data() : {
            autoCorrection: true, hapticFeedback: true, audioFeedback: false
          })
          if (this.firebase.auth().currentUser?.uid === self.uid) this.sendKeyboardSettings(settings)
          return settings
        }))
      }
      return from([])
    }))
  }

  saveChatSettings = async settings => {
    const { 
      model,
      vendor
    } = settings
    const { accountUID } = await this.getAccountScope()
    const ref = this.firebase.firestore().collection("ChatSettings").doc(accountUID)
    const updates = {}
    if (model) updates.model = model
    if (vendor) updates.vendor = vendor
    return await ref.set(updates, { merge: true })
  }

  getKeyboardSettings = async () => {
    const { accountUID } = await this.getAccountScope()
    const ref = this.firebase.firestore().collection("KeyboardSettings").doc(accountUID)
    const snap = await ref.get()
    return snap.exists ? snap.data() : {
      autoCorrection: true,
      hapticFeedback: true,
      audioFeedback: false
    }
  }

  saveSettings = async settings => {
    const { 
      autoCorrection,
      autoCorrectionLevel,
      hapticFeedback,
      audioFeedback
    } = normalizeKeyboardSettings(settings)
    const { accountUID } = await this.getAccountScope()
    const ref = this.firebase.firestore().collection("KeyboardSettings").doc(accountUID)
    const updates = {
      autoCorrection,
      autoCorrectionLevel,
      hapticFeedback,
      audioFeedback
    }
    this.sendKeyboardSettings(updates)
    await ref.set(updates, { merge: true })
  }

  getProfile = () => {
    const { photoURL, displayName } = this.self
    return {
      bio: '',
      profileImage: photoURL,
      homepage: '',
      username: displayName,
      displayName: '',
    }
  }
  getAnalytics = () => {
    return {
      followers: 0,
      following: 0,
      likes: 0,
      shared: 0
    }
  }

  uploadFileImpl = async (type, channel, file, progress) => {
    const filename =  (Math.random().toString(36).substring(2, 15) + Math.random().toString(23).substring(2, 5)) +'-'+ file.name
    consoleLog("upload file impl", filename)
    const targetRef = this.firebase.storage().ref(type).child(channel).child(filename)
    if (!file.type.startsWith('video/')) {
      // just upload directly
      const uploadTask = targetRef.put(file)
      if (progress) {
        uploadTask.on('state_changed', snap => {
          const percent = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
          debugLog("progress:", percent)
          if (progress) {
            progress(percent);
          }
        })
      }
      await uploadTask
      return targetRef
    }
    const videoDimensions = await this.getVideoDimensions(file)
    // invoke support for transcoding video
    const func = this.getFunc('api/uploads/create')
    const result = await func({
      contentType: file.type,
      type: type,
      channel: channel,
      filename: filename,
      videoDimensions
    })
    const { uploadId, path } = result.data
    const ref = this.firebase.storage().ref(path)
    const uploadTask = ref.put(file)
    if (progress) {
      uploadTask.on('state_changed', snap => {
        const percent = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
        debugLog("progress:", percent)
        if (progress) {
          progress(percent);
        }
      })
    }
    await uploadTask
    const p = new Promise((resolve, reject) => {
      const unsubscribe = this.firebase.firestore().collection('Uploads').doc(uploadId).onSnapshot(snap => {
        const data = snap.data()
        if (data) {
          switch (data.status) {
            case 'completed':
              unsubscribe()
              if (progress) progress(100)
              resolve()
              break
            case 'failed':
              unsubscribe()
              if (progress) progress(100)
              reject(new Error(data.failureReason))
              break
          }
        }
      })
    })
    await p
    return targetRef
  }

  getVideoDimensions = async file => {
    const url = URL.createObjectURL(file)
    const video = document.createElement('video')
    video.src = url
    const videoDimensions = await new Promise(resolve => {
      video.onloadedmetadata = e => {
        resolve({
          height: video.height,
          width: video.width
        })
      }
      video.load()
    })
    return await videoDimensions
  }

  uploadFileToChannel = async (channel, file, progress) => {
    return await this.uploadFileImpl('Channels', channel, file, progress)
  }
  
  uploadFile = (file, progress) => {
    consoleLog("upload file")
    return this.uploadFileImpl('Files', this.self.uid, file, progress)
  }

  uploadProfileImage = (file, progress) => {
    const ref = this.firebase.storage().ref("ProfileImages").child(this.self.uid);
    return new Promise((resolve, reject) => {
      const uploadTask = ref.put(file)
      if (progress) {
        progress(0)
      }
      uploadTask.on("state_changed", snap => {
        //debugLog("state_changed", snap);
        const percent = Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
        if (progress) {
          progress(percent)
        }
      }, reject, () => {
        return resolve(ref.getDownloadURL());
      })
    })
  }

  lastViewed = 0
  lastViewedSubject = new Subject()

  getLastViewed = () => this.lastViewed

  observeLastViewed = () => {
    return concat(of(this.lastViewed), this.lastViewedSubject)
  }

  markViewed = async move => {
    if (move.posted > this.lastViewed) {
      await updateProfile({
        lastViewed: move.posted
      })
    }
  }

  saveProfile = async updates => {
    consoleLog('saveProfile', updates)
    //debugger
    const profileUpdates = {}
    for (const field of  ['bio', 'username', 'displayName', 'profileImage']) {
      profileUpdates[field] = updates[field]
    }
    const userUpdates = {}
    for (const field of ['email', 'phoneNumber', 'password']) {
      profileUpdates[field] = updates[field] || undefined
    }
    if (profileUpdates.phoneNumber) {
      const converted = phone(profileUpdates.phoneNumber);
      profileUpdates.phoneNumber = converted[0]
    }
    const func = this.getFunc('api/account/profile')
    const result = await func(profileUpdates)
    return result.data
  }

  //@TODO remove this
  updateProfile = async updates => {
    const ref = this.firebase.firestore().collection('Users').doc(this.self.uid)
    await ref.set(updates, { merge: true })
  }

  openWindow = (url, arg) => {
    this.nativeLog('openWindow: ' + url)
    const msg = {
      type: 'openURL',
      openURL: url
    }
    if(window.electronAPI) {
      return this.electron('openURL', url)
    }
    if (this.isNative()) {
      this.nativeLog('openWindow native bridge')
      this.sendNativeMessage(msg)
      return
    }
    consoleLog("window open", url)
    return window.open(url, arg)
  }


  getChannel = uid => {
    const ids = [uid, this.self.uid]
    ids.sort()
    return ids.join('-')
  }

  observeDM = uid => {
    const id = this.getChannel(uid)
    const q = this.firebase.firestore().collection('DMs').where('to', '==', id).orderBy('ts', 'desc')
    return collectionChanges(q).pipe(flatMap(changes => {
      return changes.map(change => {
        const type = change.type
        const doc = change.doc
        const data = doc.data()
        data.id = doc.id
        return {
          type,
          comment: data
        }
      })
    }))
  }

  saveDM = async (uid, message) => {
    const func = this.getFunc('api/messages/save')
    const result = await func({to: uid, msg: message})
    return message.id = result.data.id
  }

  deleteDM = async (uid, message) => {
    const func = this.getFunc('api/messages/delete')
    const result = await func({id: message.id})
  }

  reactToDM = async (uid, message, emoji) => {
    const func = this.getFunc('api/messages/react')
    const response = await func({
      to: uid, id: message.id, emoji
    })
  }


  reqId = 0
  ops = {}

  purchaseWordPack = async (wordPack, status) => {
    return this.tokenPurchases.purchase(wordPack, status)
  }

  getAvailableTokenPacks = () => this.tokenPurchases.load()

  observeWordPacks = () => {
    return from(this.getAvailableTokenPacks()).pipe(flatMap(catalog =>
      catalog.products.map(wordPack => ({ type: 'added', wordPack }))))
  }

  deleteButton = async (button) => {
    consoleLog(button)
    const { id, buttonType } = button
    const func = this.getFunc('api/buttons/delete')
    const result = await func({id, buttonType})
    return result.data
  }

  shareButton = async (button) => {
    const { id, category, instruction } = button
    const shareData = {
      title: category + ' | ' + instruction,
      text: category + ' | ' + instruction,
      url: window.location.origin + `/shareButton?instruction=${encodeURIComponent(instruction)}&category=${encodeURIComponent(category)}&id=${id}`
    }
    consoleLog("shareData", shareData)
    try {
      result = await navigator.share(shareData)
      consoleLog(result)
    } catch (err) {
      console.error(err)
    }
  }

  observeAllWritingButtons = () => {
    return mergeN(this.observeWritingButtons(), this.observeMyWritingButtons())
  }
  
  observeWritingButtons = () => {
    const db = this.firebase.firestore()
    const ref = db.collection('WriteButtons')
    return collectionChanges(ref).pipe(flatMap(changes => {
      return changes.map(change => {
        const { type, doc } = change
        const data = doc.data()
        data.id = doc.id
        data.buttonType = 'create'
        data.isSystem = true
        return {
          button: data,
          type
        }
      })
    }))
  }

  observeMyWritingButtons = () => {
    const db = this.firebase.firestore()
    return this.observeAccountScope().pipe(switchMap(account => mergeN(...account.members.map(uid =>
      collectionChanges(db.collection('MyWriteButtons').where('uid', '==', uid))))), flatMap(changes => {
      return changes.map(change => {
        const { type, doc } = change
        const data = doc.data()
        data.id = doc.id
        data.buttonType = 'create'
        return {
          button: data,
          type
        }
      })
    }), concatMap(change => from(this.decryptButtonChange(change))))
  }

  observeAllButtons = () => {
    return mergeN(this.observeButtons(), this.observeMyButtons())
  }

  observeButtonUsage = () => {
    const db = this.firebase.firestore()
    return this.observeAccountScope().pipe(switchMap(account => combineLatest(account.members.map(uid =>
      docData(db.collection('ButtonUsage').doc(uid))))), map(values => {
        const usage = {}
        values.forEach(value => Object.entries(value || {}).forEach(([id, count]) => {
          usage[id] = (usage[id] || 0) + (Number(count) || 0)
        }))
        return usage
      }))
  }

  observeMyButtons = () => {
    const db = this.firebase.firestore()
    return this.observeAccountScope().pipe(switchMap(account => mergeN(...account.members.map(uid =>
      collectionChanges(db.collection('MyButtons').where('uid', '==', uid))))), flatMap(changes => {
      return changes.map(change => {
        const { type, doc } = change
        const data = doc.data()
        data.buttonType = 'edit'
        data.id = doc.id
        return {
          button: data,
          type
        }
      })
    }), concatMap(change => from(this.decryptButtonChange(change))))
  }

  observeButtons = () => {
    const db = this.firebase.firestore()
    const ref = db.collection('Buttons')
    return collectionChanges(ref).pipe(flatMap(changes => {
      return changes.map(change => {
        const { type, doc } = change
        const data = doc.data()
        data.id = doc.id
        data.buttonType = 'edit'
        data.isSystem = true
        return {
          button: data,
          type
        }
      })
    }))
  }

  observeAvailableWords = () => {
    const purchased = defer(() => this.observePurchases().pipe(map(createTokenBalanceAccumulator())))
    const used = this.observeUsage().pipe(map(usage => usage.tokens))
    return concat(of(0),
                  combineLatest([purchased, used]).pipe(map(([purchased, used]) => {
                    return Math.max(purchased - used, 0)
                  })))
  }

  observePurchases = () => {
    const uid = this.self.uid
    const db = this.firebase.firestore()
    return defer(() => this.tokenBilling.ensure()).pipe(
      retryWhen(errors => errors.pipe(retryDelay(5000))), switchMap(context => {
      if (this.self?.uid !== uid) return of([])
      return this.observeAccountScope().pipe(switchMap(account => mergeN(...account.members.map(member =>
        collectionChanges(db.collection(context.environment === 'Sandbox' ? 'SandboxPurchases' : 'Purchases').where('uid', '==', member))))))
    }), flatMap(changes => {
      return changes.map(change => {
        const { type, doc } = change
        const data = doc.data()
        data.id = doc.id
        return {
          wordPack: data,
          type
        }
      })
    }),catchError(err => {
      //debugger
      return from([])
    }))
  }

  saveInstruction = async (instruction, buttonType) => {
    if (await this.privateMode()) {
      const identity = this.nativeAuthGeneration.snapshot()
      const current = () => this.nativeAuthGeneration.isCurrent(identity, this.self?.uid)
      await this.synchronizeNativeIdentity()
      const {buttons} = await this.getAllButtons()
      if (!current()) throw new Error('The account changed. Please save again.')
      const existingCategories = [...new Set(buttons.filter(button=>button.buttonType === (buttonType === 'create' ? 'create' : 'edit'))
        .map(button=>button.category).filter(value=>typeof value === 'string' && value.trim() && value.length <= 48))].slice(0,100)
      const result = await this.privatePost('/api/buttons/categorize', {instruction,buttonType,existingCategories})
      const category = result.data.category
      if (!current()) throw new Error('The account changed. Please save again.')
      const id = crypto.randomUUID()
      const type = buttonType === 'create' ? 'create' : 'edit'
      const sealed = await this.nativeCall('privateCommands', {operation:'seal', id, buttonType:type, instruction, category}, 30000)
      if (!sealed?.ok) throw new Error(sealed?.error || 'Could not encrypt the command.')
      const headers = await this.requiredAuthHeaders()
      if (!current()) throw new Error('The account changed. Please save again.')
      const stored = await axios.post(`${keyboardApiServer}/api/buttons/save`, {id, buttonType:type, encrypted:sealed.encrypted}, {headers})
      if (!current()) throw new Error('The account changed after saving. Open the original account to see the command.')
      const button = stored.data?.button
      if (!button || button.id !== id) throw new Error('The command was not saved.')
      return {button:{...button,instruction,category}}
    }
    const func = this.getFunc('api/buttons/save')
    const result = await func({instruction, buttonType})
    return result.data
  }

  observeUsage = () => {
    const uid = this.self.uid
    const db = this.firebase.firestore()
    return defer(() => this.tokenBilling.ensure()).pipe(
      retryWhen(errors => errors.pipe(retryDelay(5000))), switchMap(context => {
      if (this.self?.uid !== uid) return of(null)
      return this.observeAccountScope().pipe(switchMap(account => combineLatest(account.members.map(member =>
        doc(db.collection(context.environment === 'Sandbox' ? 'SandboxUsage' : 'Usage').doc(member))))))
    }), map(snapshots => {
      const tokens = (snapshots || []).reduce((sum, snap) => sum + Math.max(0, Number(snap.data()?.tokens) || 0), 0)
      return {
        tokens,
        words: tokens
      }
    }))
  }

  fixGrammar = async (input) => {
    const { output } =  await this.edit("clean up grammar", input)
    return output || text
  }

  simplify = async (input, type) => {
    return await this.edit(`"${input}"`, 'correct wording, capitalization, and punctuation only. Don\'t respond.')
  }

  autocompleteTemplate = async (template, variable, noCache) => {
    const key = `(${template},${variable})`
    if (!noCache) {
      const value = null//localStorage.getItem(key)
      if (value) {
        return { completions: JSON.parse(value) }
      }
    }
    try {
      const result = await this.inferencePost('/api/template-autocomplete', {
	template,
	variable,
	reasoningSummary: false
      }, {
        headers: await this.requiredAuthHeaders()
      })
      const completions = (result.data.completions || [])
        .map(item => typeof item === 'string' ? item : item && item.text)
        .filter(Boolean)
      const error = result.data.error
      if (!error) {
        //localStorage.setItem(key, JSON.stringify(completions))
      }
      return { ...result.data, completions }
    } catch (err) {
      console.error(err)
    }
    return { completions: [] }
  }

  resolveReferralCode = async code => {
    const func = this.getFunc('api/referrals/redeem')
    const result = await func({code})
    return result.data
  }

  edit = async (instruction, input, buttonId, temperature) => {
    consoleLog('edit', instruction, 'input', input, 'temp', temperature)
    const data = {instruction, input, version: 'turbo', buttonId, temperature}
    const token = await this.getToken()
    const result = await this.inferencePost('/api/edit', data, {
      headers: {
        Authorization: 'Bearer ' + token
      }
    })
    return result.data
  }

  editV2 = async ({ instruction, document, requestId }) => {
    const data = { instruction, document }
    if (requestId) data.requestId = requestId
    const result = await this.inferencePost('/api/edit-v2', data, {
      headers: await this.requiredAuthHeaders()
    })
    return result.data
  }

  getLang = async input => {
    const token = await this.getToken()
    const response = await this.inferencePost('/api/lang', { input }, {
      headers: {
        Authorization: 'Bearer ' + token
      }
    })
    let { lang } = response.data
    return  resolveDialect(lang)
  }

  cancelSpeak = () => {
    this.speechRequestSeq = (this.speechRequestSeq || 0) + 1
    if (this.currentSource) {
      try {
        this.currentSource.stop()
      } catch (ignored) {
      }
      this.currentSource = null
    }
    speechSynthesis.cancel()
  }

  getVoices = async () => {
    return speechSynthesis.getVoices()
  }

  getVoiceLangs = () => {
    const Langs = {}
    if (this.googleVoices) {
      this.googleVoices.forEach(voice => {
        voice.languageCodes.forEach(lang => {
          Langs[lang] = voice
        })
      })
    } else if (this.azureVoices) {
      this.azureVoices.forEach(voice => {
        const lang = voice.Locale
        Langs[lang] = voice
      })
    } else {
      const voices = speechSynthesis.getVoices()
      voices.forEach(voice => {
        let arr = Langs[voice]
        if (!arr) {
          arr = [voice]
          Langs[voice.lang] = arr
        } else {
          arr.push(voice)
        }
      })
    }
    return Langs
  }

  resetAudioSource = () => {
    if (!this.currentAudioContext) {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      this.currentAudioContext = audioContext
      unmute(this.currentAudioContext)
    }
    if (this.currentSource) {
      try {
        this.currentSource.stop()
      } catch (ignored) {
      }
    }
    const source = this.currentAudioContext.createBufferSource()
    source.connect(this.currentAudioContext.destination)
    this.currentSource = source
  }

  systemSpeak = (text, lang) => {
    speechSynthesis.cancel()
    return new Promise(resolve => {
      const utterThis = new SpeechSynthesisUtterance(text)
      consoleLog("setting utterance lang", lang)
      const dialect = resolveDialect(lang)
      if (dialect) {
        utterThis.lang = dialect.iso
      }
      const voices = speechSynthesis.getVoices().filter(voice => voice.lang === utterThis.lang)
      consoleLog(voices)
      //debugger
      let voice
      if (lang == 'en-US') {
        voice = voices.find(voice => voice.name === "Samantha")
      } else {
        voice = voices[0]
      }
      if (!voice) {
        const lang = utterThis.lang.split('-')[0] + '-'
        voice = speechSynthesis.getVoices().find(voice => voice.lang.startsWith(lang))
      }
      utterThis.voice = voice
      this.utterance = utterThis
      utterThis.onend = resolve
      utterThis.onerror = resolve
      speechSynthesis.speak(utterThis)
    })
  }

  speak = async (text, lang) => {
    const requestSeq = this.speechRequestSeq = (this.speechRequestSeq || 0) + 1
    try {
      if (hasNativeCapability('tokenPackDeviceRuntime')) {
        const status = await this.subscriptionProvidersStatus()
        if (status.selectedProvider === 'intellikey') return this.systemSpeak(text, lang)
      }
      const token = await this.getToken()
      const response = await this.inferencePost('/api/text-to-speech', {
        text,
        lang
      }, {
        responseType: 'arraybuffer',
        headers: {
          Authorization: 'Bearer ' + token,
          Accept: 'audio/wav'
        }
      })
      if (requestSeq !== this.speechRequestSeq) return

      if (!this.currentAudioContext) this.resetAudioSource()
      const audioContext = this.currentAudioContext
      if (audioContext.state === 'suspended') {
        await audioContext.resume()
      }
      const audioBytes = response.data.slice ? response.data.slice(0) : response.data
      const decodedData = await new Promise((resolve, reject) => {
        audioContext.decodeAudioData(audioBytes, resolve, reject)
      })
      if (requestSeq !== this.speechRequestSeq) return

      this.resetAudioSource()
      const source = this.currentSource
      source.buffer = decodedData
      return await new Promise(resolve => {
        source.onended = () => {
          if (this.currentSource === source) this.currentSource = null
          resolve()
        }
        source.start()
      })
    } catch (error) {
      console.error('Remote text to speech failed; using system voice', error)
      if (requestSeq !== this.speechRequestSeq) return
      return this.systemSpeak(text, lang)
    }
  }

  shouldUseWhisper = () => !!WHISPER 

  getVoiceRecognizer = () => {
    //return new VoiceRecognizer()
    if (!this.voiceRecognizer) {
      this.voiceRecognizer = this.shouldUseWhisper() ?
        new WhisperVoiceRecognizer() : 
        new SystemVoiceRecognizer(this.autocorrect)
    }
    return this.voiceRecognizer
  }

  autocorrect = async opts => {
    const {
      document,
      lang,
      timestamp,
      mode,
      cursorOffset,
      autocorrectApplyThreshold,
      signal
    } = opts
    const response = await this.inferencePost('/api/autocorrect', autocorrectPayload({
      document,
      cursorOffset,
      lang,
      timestamp,
      mode,
      autocorrectApplyThreshold,
      autocorrectLevel: effectiveAutocorrectionLevel(this.keyboardSettings)
    }), {
      headers: isIntelliKeyMacApp() ? {} : await this.requiredAuthHeaders(),
      signal
    })
    return response.data
  }

  autocomplete = async ({
    document,
    cursorOffset,
    lang,
    mode,
    selectionStart,
    selectionEnd,
    onUpdate,
    signal
  }) => {
    const input = autocompletePayload({
      document,
      cursorOffset,
      lang,
      mode,
      selectionStart,
      selectionEnd
    })
    const native = isIntelliKeyMacApp() || await this.privateMode()
    if (!native && isDesktop()) throw new Error('Open IntelliKey’s Mac app to use autocomplete.')
    const token = native ? null : await this.getToken()
    const billingHeaders = native ? {} : await this.tokenBilling.headers()
    return new Promise((resolve, reject) => {
      const read = autocompleteStreamReader(onUpdate)
      let content = '', request, settled = false
      const finish = (error, result) => {
        if (settled) return
        settled = true
        signal?.removeEventListener('abort', cancel)
        if (error) reject(error)
        else resolve(result)
      }
      const cancel = () => {
        request?.abort()
        finish(new DOMException('Request cancelled.', 'AbortError'))
      }
      if (signal?.aborted) return cancel()
      signal?.addEventListener('abort', cancel, { once: true })
      const callbacks = {
        headers: billingHeaders,
        onContent: value => {
          if (settled) return
          content = value
          try { read(content) } catch (error) { request?.abort(); finish(error) }
        },
        onDone: () => {
          try {
            const result = read(content, true)
            if (mode !== 'selection' && !result.candidates.length && !result.noCandidateReasons) {
              throw new Error('Couldn’t load suggestions. Please try again.')
            }
            finish(null, result)
          } catch (error) { finish(error) }
        },
        onError: error => finish(error)
      }
      try {
        request = native ? this.privateStream('/api/autocomplete-jsonl', input, callbacks)
          : streamReply(`${keyboardApiServer}/api/autocomplete-jsonl`, input, token, callbacks)
      } catch (error) { finish(error) }
    })
  }

  getSafeAreaInsetTop = () => {
    const result =
          getComputedStyle(document.documentElement).getPropertyValue("--sat")
    return parseFloat(result.substring(0,
                                       result.length-2))
  }

  getSafeAreaInsetBottom = () => {
    const result =
          getComputedStyle(document.documentElement).getPropertyValue("--sab")
    return parseFloat(result.substring(0,
                                       result.length-2))
  }

  getSafeAreaInsetLeft = () => {
    const result =
          getComputedStyle(document.documentElement).getPropertyValue("--sal")
    return parseFloat(result.substring(0,
                                       result.length-2))
  }

  receiveSharedButton = async (buttonId) => {
    //debugger
    const func = this.getFunc('api/buttons/receive')
    const data = {buttonId}    
    await func(data)
  }

  deleteAccount = async () => {
    const session = this.nativeAuthGeneration.snapshot()
    const isCurrent = () => this.nativeAuthGeneration.isCurrent(session, this.firebase.auth().currentUser?.uid)
    this.assertCurrentAccount(session)
    const native = hasNativeMessageBridge()
    const links = await this.getAccountLinks()
    this.assertCurrentAccount(session)
    const appleLinked = links.identities.some(provider => provider.providerId === 'apple.com')
    if (appleLinked && native) {
      if (!hasNativeCapability('appleSignIn')) throw new Error('Update IntelliKey to delete an account linked to Apple.')
      await this.synchronizeNativeIdentity()
    }
    const confirmed = await revokeAppleForAccountDeletion({
      firebase: this.firebase,
      linkedAccounts: links.identities,
      isCurrent,
      nativeRevoke: native ? request => this.nativeCall('revokeAppleSignIn', request, 5 * 60 * 1000) : null
    })
    if (!confirmed) return false
    this.assertCurrentAccount(session)
    const func = this.getFunc('api/account/delete')
    await func()
    this.assertCurrentAccount(session)
    await this.signOut()
    return true
  }

  updatePhoneNumber = async (phoneNumber) => {
    await delay(5.0)
  }

  // stripe related

  cancelCustomerSetupIntent = async (setupIntent) => {
    const id = setupIntent.id
    const func = this.getFunc('api/purchases/stripe/setup/cancel')
    const arg = {id}
    const result = await func(arg)
    return result.data
  }
  
  createCustomerSetupIntent = async () => {
    const func = this.getFunc('api/purchases/stripe/setup/create')
    const arg = {}
    const result = await func(arg)
    return result.data
  }

  removeCustomerPaymentMethod = async paymentMethod => {
    const func = this.getFunc('api/purchases/stripe/payment-method/remove')
    const arg = {id: paymentMethod.id}
    const result = await func(arg)
    return result.data
  }

  purchaseApi = async (path, input, signal) => {
    const session = this.nativeAuthGeneration.snapshot()
    this.tokenPurchases.assertCurrent(session)
    const token = await this.getToken()
    this.tokenPurchases.assertCurrent(session)
    const response = await axios.post(`${keyboardApiServer}/api/purchases/${path}`, input, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 30000, signal
    })
    this.tokenPurchases.assertCurrent(session)
    return response.data
  }

  createCustomerPaymentIntent = productId => this.purchaseApi('stripe/payment/create', { productId })

  getPaymentMethod = async id => {
    const func = this.getFunc('api/purchases/stripe/payment-method/get')
    const arg = {id}
    const result = await func(arg)
    return result.data
  }

  getPaymentMethods = async id => {
    const func = this.getFunc('api/purchases/stripe/payment-methods/list')
    const arg = {}
    const result = await func(arg)
    return result.data
  }
  
  selectCustomerPaymentMethod = async paymentMethod => {
    const func = this.getFunc('api/purchases/stripe/payment-method/select')
    const arg = { paymentMethodId: paymentMethod.id }
    const result = await func(arg)
    return result.data
  }
  observeCustomer = () => {
    return doc(this.firebase.firestore().collection('Customers').doc(this.self.uid)).pipe(map(snap => {
      return snap.exists ? snap.data() : null
    }))
  }

  observePaymentMethods = () => {
    return this.observeCustomer().pipe(map(customer => {
      //debugger
      if (customer) {
        let { paymentMethods, selectedPaymentMethod } = customer
        if (paymentMethods && selectedPaymentMethod) {
          selectedPaymentMethod = paymentMethods.find(x => x.id === selectedPaymentMethod)
        }
        return { paymentMethods, selectedPaymentMethod }
      }
      return {}
    }))
  }

  reportProgress = async state => {
    if (!this.isNative()) {
      return
    }
    const db = this.firebase.firestore()
    if (!this.installation) {
      const ref = db.collection("Installation").doc(this.self.uid)
      const snap = await ref.get()
      this.installation = snap.exists ? snap.data() : {}
    }
    if (this.installation[state]) {
      return
    }
    this.installation[state] = true
    const ref = db.collection("Installation").doc(this.self.uid)
    const updates = {}
    updates[state] = true
    await ref.set(updates, { merge: true })
  }

  handleReferral = uid => {
  }

  handleInvitation = link => {
  }

  decryptButtonChange = async change => {
    if (change.type === 'removed') return change
    try {
      const [button] = await this.decryptButtons([change.button])
      if (!button) return { ...change, type: 'removed' }
      return {...change, button}
    } catch (error) {
      return {...change, type:'error', error}
    }
  }

  decryptButtons = async buttons => {
    if (!buttons.some(button=>button.encrypted)) return buttons
    if (!hasNativeCapability('privateInference')) throw new Error('Open these encrypted commands on the iPhone where they were saved.')
    await this.synchronizeNativeIdentity()
    const session = this.nativeAuthGeneration.snapshot()
    const opened = []
    for (let offset = 0; offset < buttons.length; offset += 1000) {
      this.assertCurrentAccount(session)
      const result = await this.nativeCall('privateCommands', {operation:'open',buttons:buttons.slice(offset, offset + 1000)}, 30000)
      this.assertCurrentAccount(session)
      if (!result?.ok) throw new Error(result?.error || 'Could not unlock the saved commands.')
      opened.push(...result.buttons)
    }
    return opened
  }

  getAllButtons = async () => {
    const func = this.getFunc('api/buttons/list')
    const result = await func({})
    return {...result.data, buttons:await this.decryptButtons(result.data.buttons || [])}
  }

  getReferralCode = async () => {
    consoleLog('getReferralCode', this.referralCode)
    if (!this.referralCode) {
      const func = this.getFunc('api/referrals/code')
      const result = await func({})
      const data = result.data
      consoleLog('getReferralCode result', data)
      const { code } = data
      this.referralCode = code || null
    }
    return this.referralCode
  }

  getApiKey = async () => {
    if (!this.apiKey) {
      const token = await this.getToken()
      const result = await axios.post(`${keyboardApiServer}/getApiKey`, {}, {
        headers: {
          Authorization: 'Bearer ' + token
        }
      })
      const data = result.data
      consoleLog('getApiKey result', data)
      const { apiKey } = data
      this.apiKey = apiKey || null
    }
    return this.apiKey
  }

  getInviteCode = () => {
    return InviteCode
  }

  redirectToAppStore() {
    this.openWindow("https://apps.apple.com/us/app/intellikey/id1667188645")
  }

  checkForServerConnectivity = async () => {
    let result = false
    consoleLog("origin", window.location.origin)
    if (window.location.host.startsWith("localhost")) {
      result = Math.random () > 0.5 ? true : false
    } else {
      const file = window.location.origin + '/index.html'
      const  randomNum = Math.round(Math.random() * 10000)
      try {
        const response = await axios.head(file + "?rand=" + randomNum)
        consoleLog("head result", response.status)
        result = true
        const now = Date.now()
        if (this.lastServerReachableTime > 0 &&
            now - this.lastServerReachableTime > 60000) {
          //window.location.reload()
        }          
        this.lastServerReachableTime = now
      } catch (err) {
        consoleLog(err.message)
      }
    }
    this.serverReachable = result
    this.serverConnectivitySubject.next(result)
  }

  doCheckForServerConnectivity = async () => {
    this.serverReachable = result
    this.serverConnectivitySubject.next(result)
  }
  lastServerReachableTime = 0
  serverConnectivitySubject = new Subject()
  observeServerConnectivity = () => {
    if (true) {
      return of(true)
    }
    if (!this.serverConnectivityTimer) {
      let interval = 15000
      if (window.location.host.startsWith("localhost")) {
        interval = 3000
      }
      this.serverConnectivityTimer = setInterval(this.checkForServerConnectivity, interval)
      this.checkForServerConnectivity()
    }
    if (this.serverReachable !== undefined) {
      return concat(of(this.serverReachable), this.serverConnectivitySubject)
    }
    return this.serverConnectivitySubject
  }

  getMessagesRef = (uid = this.accountLinks?.accountUID || this.self.uid) => this.firebase.firestore().collection('ChatGPT').doc(uid).collection('messages')
  getThreadsRef = (uid = this.accountLinks?.accountUID || this.self.uid) => this.firebase.firestore().collection('ChatGPT').doc(uid).collection('threads')
  getTasksRef = (uid = this.accountLinks?.accountUID || this.self.uid) => this.firebase.firestore().collection('UserChannel').doc(uid).collection('Tasks')

  searchChatMessages = async (q, topic, model) => {
    if (!q && !topic || topic === 'new-thread') {
      return {
        results: [],
        page: 0,
        out_of: 0
      }
    }
    const func = await this.getFunc("searchChatMessages")
    const response = await func({q, topic, model})
    const { results } = response.data
    results.forEach(result => {
      if (Array.isArray(result.reaction)) {
        result.reaction = result.reaction.join('')
      }
    })
    return response.data
  }

  searchChatThreads = async q => {
    const func = await this.getFunc("searchChatThreads")
    const response = await func({q})
    return response.data
  }

  observeChatMessages = (model) => {
    return this.observeChatMessagesImpl({model, limit: 15})
  }

  observeTaskMessages = (opts) => {
    return this.observeChatMessagesImpl(opts)
  }

  observeChatMessagesImpl = (opts) => {
    const { model, task, limit } = opts
    const query = uid => {
      let q = this.getMessagesRef(uid)
      if (model) q = q.where('model', '==', model)
      if (task) q = q.where('task', '==', task)
      q = q.orderBy('ts', 'desc')
      if (limit) {
        q = q.limit(limit)
    }
    return q
    }
    let ob = this.observeAccountScope().pipe(switchMap(account => mergeN(...account.members.map(uid => collectionChanges(query(uid))))), flatMap(changes => {
      console.log(changes)
      return changes.map(change => {
        const { type, doc } = change
        const data = doc.data()
        if (!data.text) {
          data.text = ''
        }
        data.id = doc.id
        if (Array.isArray(data.reaction)) {
          data.reaction = data.reaction.join('')
        } 
        return {
          type,
          message: data
        }
      })
    }))
    return ob
  }

  getThreadsHistory = async (model, earliest, limit) => {
    const { members } = await this.getAccountScope()
    const snapshots = await Promise.all(members.map(async uid => {
      let q = this.getThreadsRef(uid)
      q = q.where("model", '==', model)
      q = q.orderBy('lastUpdated', 'desc').where("lastUpdated", '<', earliest).limit(limit)
      return q.get()
    }))
    const docs = snapshots.flatMap(snapshot => snapshot.docs).sort((a, b) => Number(b.data().lastUpdated) - Number(a.data().lastUpdated)).slice(0, limit)
    return docs.map(doc  => {
      const data = doc.data()
      data.id = doc.id
      return data
    })
  }
  
  observeChatThreads = (opts) => {
    const { model, task } = opts
    const query = uid => {
      let q = this.getThreadsRef(uid)
      if (task) q = q.where('task', '==', task)
      if (model) q = q.where('model', '==', model)
      return q.orderBy('lastUpdated', 'desc').limit(50)
    }
    return this.observeAccountScope().pipe(switchMap(account => mergeN(...account.members.map(uid => collectionChanges(query(uid))))), flatMap(changes => {
      return changes.map(change => {
        const { type, doc } = change
        const data = doc.data()
        data.id = doc.id
        return {
          type,
          thread: data
        }
      })
    }))
  }

  observeFunctions = () => {
    
  }

  observeTasks = (model) => {
    const query = uid => {
      let q = this.getTasksRef(uid)
      q = q.orderBy('lastUpdated', 'desc').limit(50)
      return q
    }
    return this.observeAccountScope().pipe(switchMap(account => mergeN(...account.members.map(uid => collectionChanges(query(uid))))), flatMap(changes => {
      return changes.map(change => {
        const { type, doc } = change
        const data = doc.data()
        data.id = doc.id
        if (!data.topic) {
          data.topic = data.lastTopic.topic
        }
        return {
          type,
          task: data,
        }
      })
    }))
  }
  
  waitForStripePurchase = (paymentIntent, signal) => {
    const session = this.nativeAuthGeneration.snapshot()
    return waitForStripeFulfillment({
      signal,
      assertCurrent: () => this.tokenPurchases.assertCurrent(session),
      check: requestSignal => this.purchaseApi('stripe/payment/status', { id: paymentIntent.id }, requestSignal)
    })
  }
  
  observeSubthreads = (topicId) => {
    const query = uid => {
      const q = this.getThreadsRef(uid).where('subtopic', '==', topicId).orderBy('lastUpdated', 'desc').limit(50)
      return q
    }
    return this.observeAccountScope().pipe(switchMap(account => mergeN(...account.members.map(uid => collectionChanges(query(uid))))), flatMap(changes => {
      return changes.map(change => {
        const { type, doc } = change
        const data = doc.data()
        data.id = doc.id
        return {
          type,
          thread: data
        }
      })
    }))
  }
  
  getHistory = async (model, ts, limit) => {
    const { members } = await this.getAccountScope()
    const snapshots = await Promise.all(members.map(async uid => {
      let q = this.getMessagesRef(uid)
      q = q.where('model', '==', model)
      q = q.orderBy("ts", "desc").where("ts", "<", ts).limit(limit*2)
      return q.get()
    }))
    const docs = snapshots.flatMap(snapshot => snapshot.docs).sort((a, b) => Number(b.data().ts) - Number(a.data().ts)).slice(0, limit * 2)
    return docs.map(snap => {
      const data = snap.data()
      data.id = snap.id
      if (Array.isArray(data.reaction)) {
        data.reaction = data.reaction.join('')
      }
      return data
    })
  }

  getTaskHistory = async (taskId, ts, limit) => {
    //debugger
    const { members } = await this.getAccountScope()
    const snapshots = await Promise.all(members.map(async uid => {
      let q = this.getMessagesRef(uid)
      q = q.where('task', '==', taskId)
      q = q.orderBy("ts", "desc").where("ts", "<", ts).limit(limit*2)
      return q.get()
    }))
    const docs = snapshots.flatMap(snapshot => snapshot.docs).sort((a, b) => Number(b.data().ts) - Number(a.data().ts)).slice(0, limit * 2)
    return docs.map(snap => {
      const data = snap.data()
      data.id = snap.id
      return data
    })
  }
  
  getToken = () => this.self.getIdToken(false)

  requiredAuthHeaders = () => requiredFirebaseAuthHeaders(() => this.getToken())

  openAICodexOAuthStatus = async () => {
    const func = this.getFunc('api/openai-codex/oauth/status')
    const response = await func({})
    return response.data
  }

  startOpenAICodexOAuth = async (opts = {}) => {
    const func = this.getFunc('api/openai-codex/oauth/start')
    const response = await func({
      flow: opts.flow || opts.method
    })
    return response.data
  }

  startNativeOpenAICodexOAuthCallbackServer = async (info) => {
    if (!this.isNative() || window.electronAPI) return null
    if (!info || !info.redirectUri) {
      throw new Error('OpenAI sign-in did not provide a callback address.')
    }
    const url = new URL(info.redirectUri)
    if (url.protocol !== 'http:' ||
        url.hostname !== 'localhost' ||
        url.port !== '1455' ||
        url.pathname !== '/auth/callback') {
      throw new Error('OpenAI sign-in returned an unsupported callback address.')
    }
    const response = await this.nativeCall('startOpenAICodexOAuthCallbackServer', {
      redirectUri: info.redirectUri,
      port: Number(url.port),
      path: url.pathname
    })
    if (response && response.ok === false) {
      throw new Error(response.error || 'Native OpenAI OAuth callback server failed to start')
    }
    return response
  }

  initiateOpenAIOAuthLogin = async (opts = {}) => {
    const info = await this.startOpenAICodexOAuth(opts)
    await this.startNativeOpenAICodexOAuthCallbackServer(info)
    const url = info.authorizationUrl || info.authUrl || info.verificationUri
    if (opts.openVerificationPage !== false && url) {
      this.openWindow(url, '_blank')
    }
    return info
  }

  completeOpenAIOAuthLogin = async (codeOrUrl) => {
    const func = this.getFunc('api/openai-codex/oauth/complete')
    const response = await func({
      code: codeOrUrl,
      url: codeOrUrl,
      redirectUrl: codeOrUrl
    })
    return response.data
  }

  pollOpenAICodexOAuth = async () => {
    const func = this.getFunc('api/openai-codex/oauth/poll')
    const response = await func({})
    return response.data
  }

  disconnectOpenAICodexOAuth = async () => {
    const func = this.getFunc('api/openai-codex/oauth/disconnect')
    const response = await func({})
    return response.data
  }

  privateStreams = new Map()

  privateStream = (path, input, opts) => {
    if (!hasNativeCapability('privateInference')) throw new Error('Update IntelliKey to use Private for this feature.')
    const id = `private-${++this.callId}`
    const authObservation = this.nativeAuthGeneration.snapshot()
    let content = '', cancelled = false
    const pending = { authObservation, onContent: chunk => {
      if (cancelled) return
      content += chunk
      opts.onContent(content)
    } }
    this.privateStreams.set(id, pending)
    const xhr = { status: 200, responseText: '', abort: () => {
      cancelled = true
      this.nativeCall('privateInference', {id, cancel:true}).catch(() => {})
    } }
    this.synchronizeNativeIdentity().then(async () => {
      if (!await this.privateMode()) await this.tokenBilling.ensure()
    }).then(() => {
      if (cancelled) throw new Error('Request cancelled.')
      if (!this.nativeAuthGeneration.isCurrent(authObservation, this.self?.uid)) throw new Error('The IntelliKey account changed.')
      return this.nativeCall('privateInference', {id, path, input}, 180000)
    }).then(result => {
      if (!result?.ok) throw new Error(result?.error || 'The private request failed.')
      xhr.responseText = content
      if (!cancelled) opts.onDone(xhr)
    }).catch(error => {
      if (!cancelled) opts.onError(error, 0, xhr)
    }).finally(() => {
      this.privateStreams.delete(id)
      this.nativeCall('privateInference', {id,cancel:true}).catch(() => {})
    })
    return xhr
  }

  inferencePost = async (path, input, configuration = {}) => {
    if (isIntelliKeyMacApp()) return this.privatePost(path, input, configuration)
    if (!hasNativeMessageBridge()) throw new Error('Open IntelliKey’s app to use AI editing.')
    if (!await this.privateMode()) return axios.post(`${keyboardApiServer}${path}`, input, {
      ...configuration, headers: { ...configuration.headers, ...await this.tokenBilling.headers() }
    })
    return this.privatePost(path, input, configuration)
  }

  privatePost = (path, input, configuration = {}) => {
    return new Promise((resolve, reject) => {
      let content = '', request
      const signal = configuration.signal
      const cancel = () => {
        request?.abort()
        reject(new DOMException('Request cancelled.', 'AbortError'))
      }
      if (signal?.aborted) return cancel()
      signal?.addEventListener('abort', cancel, { once: true })
      const cleanup = () => signal?.removeEventListener('abort', cancel)
      request = this.privateStream(path, input, {
        onContent: value => { content = value },
        onDone: () => {
          cleanup()
          try {
            let data = JSON.parse(content)
            if (configuration.responseType === 'arraybuffer') {
              if (typeof data.audioBase64 !== 'string') throw new Error('Missing private speech audio')
              const decoded = atob(data.audioBase64)
              data = Uint8Array.from(decoded, ch=>ch.charCodeAt(0)).buffer
            }
            resolve({data})
          } catch { reject(new Error('The private provider returned an invalid result.')) }
        },
        onError: error => { cleanup(); reject(error) }
      })
    })
  }

  privateMode = async enabled => {
    if (!hasNativeCapability('localOpenAISubscription')) return false
    const options = typeof enabled === 'boolean' ? { enabled } : {}
    const result = await this.localOpenAISubscription('privateMode', options)
    if (typeof result.enabled !== 'boolean') throw new Error('Could not read the Private setting. Please try again.')
    return result.enabled
  }

  localOpenAISubscription = async (operation, options = {}) => {
    if (operation !== 'privateMode' || hasNativeCapability('deviceSubscriptions') ||
        (this.self?.uid && hasNativeCapability('clearSubscriptionsOnSignOut'))) await this.synchronizeNativeIdentity()
    const result = await this.nativeCall('localOpenAISubscription', { operation, ...options }, operation === 'connectAPIKey' ? 300000 : 45000)
    if (!result || result.ok !== true) throw new Error((result && result.error) || 'Could not update the subscription on this device.')
    return result
  }

  subscriptionProvidersStatus = () => readInferenceProviderSelection(this, hasNativeCapability('inferenceProviderSelection'), hasNativeCapability('deviceSubscriptions'))

  supportsDeviceNetwork = () => hasNativeCapability('deviceNetwork')

  deviceNetwork = async (operation = 'status', options = {}) => {
    if (!this.supportsDeviceNetwork()) throw new Error('Update IntelliKey to connect your devices.')
    await this.synchronizeNativeIdentity()
    const result = await this.nativeCall('deviceNetwork', { operation, ...options }, 60000)
    if (!result?.ok) throw new Error(result?.error || 'Could not connect your devices.')
    return result
  }

  observeSubscriptionCatalog = (onCatalog, onError) => {
    const db = this.firebase.firestore()
    let providerDocuments = []
    let modelDocuments = []
    let providersLoaded = false
    let modelsLoaded = false
    const emit = () => {
      if (!providersLoaded || !modelsLoaded) return
      const providers = providerDocuments
        .filter(provider => provider.subscription && provider.subscription.enabled !== false && !provider.subscription.hidden)
        .sort((left, right) => Number(left.subscription.order || 0) - Number(right.subscription.order || 0))
        .map(provider => {
          const subscription = provider.subscription || {}
          const models = []
          for (const model of modelDocuments) {
            for (const route of model.inferenceProviders || []) {
              if (route.id !== provider.id) continue
              const id = route.runtimeModelId || route.model || model.id
              models.push({
                id,
                name: model.name || model.title || id,
                note: route.note || '',
                recommended: Boolean(route.recommended || id === subscription.defaultModel),
                order: Number(route.order || 0)
              })
            }
          }
          return {
            id: provider.id,
            kind: subscription.kind,
            name: provider.name,
            description: provider.description || '',
            oauthAvailable: subscription.oauthAvailable !== false,
            oauthDisplayName: subscription.oauthDisplayName,
            unavailableReason: subscription.unavailableReason,
            documentationUrl: subscription.documentationUrl,
            source: subscription.source,
            dynamicModels: Boolean(subscription.dynamicModels),
            defaultModel: subscription.defaultModel,
            models: models.sort((left, right) => left.order - right.order)
          }
        })
      onCatalog(providers)
    }
    const providerUnsubscribe = db.collection('Providers')
      .where('subscriptionCatalog', '==', 'intellikey')
      .onSnapshot(snapshot => {
        providerDocuments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        providersLoaded = true
        emit()
      }, onError)
    const modelUnsubscribe = db.collection('Models')
      .where('subscriptionCatalog', '==', 'intellikey')
      .onSnapshot(snapshot => {
        modelDocuments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        modelsLoaded = true
        emit()
      }, onError)
    return () => {
      providerUnsubscribe()
      modelUnsubscribe()
    }
  }

  selectSubscriptionProviderModel = (provider, model, activate = true) =>
    selectInferenceProvider(this, hasNativeCapability('inferenceProviderSelection'), provider, model, activate, hasNativeCapability('deviceSubscriptions'))

  startSubscriptionProviderOAuth = async (provider, flow) => {
    if (!this.isNative()) {
      throw new Error('Connect providers from the native IntelliKey app.')
    }
    const func = this.getFunc('api/subscriptions/connect')
    const response = await func({ provider, flow })
    return response.data
  }

  initiateSubscriptionProviderOAuth = async provider => {
    if (await usesDeviceSubscriptions(this, hasNativeCapability('inferenceProviderSelection'), undefined, hasNativeCapability('deviceSubscriptions'))) {
      return this.localOpenAISubscription('connect', { provider })
    }
    const flow = provider === 'openai' && window.electronAPI
      ? 'device_code'
      : 'browser'
    const info = await this.startSubscriptionProviderOAuth(provider, flow)
    if (provider === 'openai') {
      await this.startNativeOpenAICodexOAuthCallbackServer(info)
    }
    const url = info.authorizationUrl || info.authUrl || info.verificationUri
    if (url) this.openWindow(url, '_blank')
    return info
  }

  connectAWSProvider = provider => {
    if (!this.isNative()) throw new Error('Open IntelliKey on your device to enter AWS credentials.')
    return this.localOpenAISubscription('connectAPIKey', { provider })
  }

  pollSubscriptionProviderOAuth = async provider => {
    if (await usesDeviceSubscriptions(this, hasNativeCapability('inferenceProviderSelection'), undefined, hasNativeCapability('deviceSubscriptions'))) {
      return this.localOpenAISubscription('poll', { provider })
    }
    const func = this.getFunc('api/subscriptions/poll')
    const response = await func({ provider })
    return response.data
  }

  disconnectSubscriptionProvider = async provider => {
    if (await usesDeviceSubscriptions(this, hasNativeCapability('inferenceProviderSelection'), undefined, hasNativeCapability('deviceSubscriptions'))) {
      return this.localOpenAISubscription('disconnect', { provider })
    }
    const func = this.getFunc('api/subscriptions/disconnect')
    const response = await func({ provider })
    return response.data
  }

  shouldTraceChatGPT = () => {
    return window.location.hostname.startsWith('localhost')
  }

  assertCurrentAccount = session => {
    if (!session.authUID || !this.nativeAuthGeneration.isCurrent(session, this.firebase.auth().currentUser?.uid)) {
      throw new Error('The IntelliKey account changed. Please try again.')
    }
  }

  getFunc = name => {
    return async (data) => {
      const session = this.nativeAuthGeneration.snapshot()
      this.assertCurrentAccount(session)
      const headers = await requiredFirebaseAuthHeaders(() => this.getToken())
      // Subscription snapshots include IntelliKey's available Tokens. Use the
      // same verified environment as the balance counter and inference requests.
      if (['api/buttons/save', 'sendChat', 'api/subscriptions/status', 'api/subscriptions/select'].includes(name)) {
        Object.assign(headers, await this.tokenBilling.headers())
      }
      this.assertCurrentAccount(session)
      const url = `${keyboardApiServer}/${name}`
      consoleLog('name', data)
      const response = await axios.post(url, data, {
        headers
      })
      this.assertCurrentAccount(session)
      consoleLog("result", name, response.data)
      return response
    }
  }

  getPublicFunc = name => {
    return async (data) => {
      const url = `${keyboardApiServer}/${name}`
      const response = await axios.post(url, data)
      consoleLog("result", name, response.data)
      return response
    }
  }

  observeUsageAnalytics = ({ from, to }, next, error) => {
    const ref = this.firebase.firestore()
      .collection('UserChannel')
      .doc(this.self.uid)
      .collection('UsageHourly')
      .where('bucketStart', '>=', Number(from))
      .where('bucketStart', '<', Number(to))
      .orderBy('bucketStart', 'asc')
    return ref.onSnapshot(snapshot => {
      next(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })))
    }, error)
  }

  deleteChatMessage = async id => {
    const func = this.getFunc('deleteChatMessage')
    const response = await func({id})
    return response.data
  }

  createNewTask = () => {
    const ref = this.getTasksRef().doc()
    const data = {
      lastTopic: {
        topic: 'New Task',
        topicId: 'new-topic'
      },
      lastUpdated: Date.now()
    }
    ref.set(data)
    data.id = ref.id
    return data
  }

  streamChat = async (msg, isNewTopic, opts) => {
    const token = await this.getToken()
    const url = `${keyboardApiServer}/streamChat`
    const dup = clone(msg)
    dup.thread = dup.topic
    delete dup.topic
    dup.utcOffset = this.utcOffset
    dup.isNewTopic = isNewTopic
    dup.noMarkdown = true;
    dup.model = opts.model
    dup.platform = this.platform
    dup.osVersion = this.osVersion
    dup.shell = this.shell
    dup.isHelpful = this.isHelpful()
    dup.assistantModel = opts.assistantModel
    dup.debug = this.isAdmin
    if (await this.privateMode()) return this.privateStream('/streamChat', dup, opts)
    return streamReply(url, dup, token, { ...opts, headers: await this.tokenBilling.headers() })
  }

  sendChat = async (msg, isNewTopic, factCheck) => {
    const func = this.getFunc('sendChat')
    const dup = clone(msg)
    dup.thread = dup.topic
    delete dup.topic
    dup.utcOffset = this.utcOffset
    dup.isNewTopic = isNewTopic
    dup.factCheck = factCheck
    dup.platform = this.platform
    dup.osVersion = this.osVersion
    dup.shell = this.shell
    const response = await this.privateMode() ? await this.inferencePost('/sendChat', dup) : await func(dup)
    return response.data || {}
  }



  streamEdit = async (instruction, input, buttonId, temperature, max_tokens, opts) => {
    const url = `${keyboardApiServer}/streamEdit`
    const token = await this.getToken()
    if (isIntelliKeyMacApp() || await this.privateMode()) return this.privateStream('/streamEdit', {instruction, input, buttonId, temperature, max_tokens}, opts)
    return streamReply(url, {instruction, input, buttonId, temperature, max_tokens}, token, { ...opts, headers: await this.tokenBilling.headers() })
  }



  isHelpful = () => false
  isBaseModel = () => false

  isAsk = undefined
  
  isAskAppOnly = () => false


  receiveSharedThread = async (from, threadId) => {
    const func = this.getFunc('shareThread')
    await func({
      thread: threadId,
      from: from
    })
  }

  shareThread = async (thread, messages) => {
    let  origin = window.location.origin
    const url =  origin + `/shareThread?topic=${encodeURIComponent(thread.topic)}&from=${this.self.uid}&id=${thread.id}`
    if (!isDesktop() && navigator.share) {
      const shareData = {
        title: thread.topic,
        text: thread.topic,
        url,
      }
      consoleLog("shareData", shareData)
      try {
        result = await navigator.share(shareData)
        consoleLog(result)
      } catch (err) {
        console.error(err)
      }
    } else {
      navigator.clipboard.writeText(url)
      await delay(0.5)
    }
    return url
  }

  electronCallId = 0
  electronCalls = {}
  electron = (type, value) => {
    if (window.electronAPI) {
      return new Promise((resolve, reject) => {
        const id = ++this.electronCallId
        const msg = {
          id,
          type,
        }
        msg[type] = value
        this.electronCalls[id] = {
          resolve, reject
        }
        window.electronAPI.sendMessage(JSON.stringify(msg))
        window.electronAPI.onReply(arg => {
          window.electronReply(arg)
        })
      })
    }
  }

  deleteTask = async task => {
    const func = this.getFunc('deleteTask')
    await func({ taskId: task.id })
  }
}
